/**
 * Voice chat module for Ragsmith.
 *
 * Architecture: WebSocket handles audio I/O only (STT + TTS).
 * Chat logic goes through the normal sendMessage() flow so all features
 * (tools, web search, agent/collection logic, spinner, citations, etc.)
 * work identically to text chat.
 *
 * Flow: mic → AudioWorklet → WebSocket(VAD+STT) → transcript →
 *       sendMessage() → normal SSE chat → onComplete → WebSocket(TTS) → speaker
 */

import { state, API_BASE, saveNarratorProgress } from './state.js';
import { elements, addMessage, scrollToBottom, forceScrollToBottom, smoothScrollToBottom, updateModeIndicators } from './ui.js';
import { sendMessage } from './chat.js';
import { getHeaders, cancelChat } from './api.js';
import { getSelectedVoice, getTTSOptions } from './voice-manager.js';
import { isMobileBrowser, silentWavUrl } from './silent-audio.js';
import { getAuthToken } from './auth.js';
// TTS cache removed — progressive caching caused browser teardown delays

// ─── Constants ───────────────────────────────────────────────────────────────

const VOICE_STATES = {
    IDLE: 'idle',
    TRANSCRIBING: 'transcribing',
    CONNECTING: 'connecting',
    LISTENING: 'listening',
    USER_SPEAKING: 'user_speaking',
    PROCESSING: 'processing',
    BUFFERING: 'buffering',
    RECONNECTING: 'reconnecting',  // Connection dropped, attempting recovery
    PAUSED: 'paused',
    ASSISTANT_SPEAKING: 'assistant_speaking',
};

const INPUT_SAMPLE_RATE = 16000;  // What the server expects
const TTS_SAMPLE_RATE = 24000;    // Kokoro TTS output (overridden by server)

// ─── Module State ────────────────────────────────────────────────────────────

let voiceState = VOICE_STATES.IDLE;
let voiceGeneration = 0;          // Incremented on every stop; invalidates stale async work
let ws = null;                    // WebSocket connection
let audioContext = null;          // For mic capture
let mediaStream = null;           // getUserMedia stream
let workletNode = null;           // AudioWorkletNode
let sourceNode = null;            // MediaStreamAudioSourceNode

let playbackContext = null;       // For TTS playback
let playbackTime = 0;             // Next scheduled playback time
// A research answer is generated far faster than it is spoken, so chunks
// arrive much quicker than real time. Handing every one straight to
// source.start() left hundreds of live AudioBufferSourceNodes scheduled on
// the render thread at once, and that backlog only grew for the length of
// the utterance -- clean for the first seconds, then a faint static that
// worsened steadily as the queue deepened. Hold arriving audio here and
// schedule it only when its turn is near, so the node count stays flat
// however long the answer runs.
const MAX_SCHEDULE_AHEAD_SEC = 2.0;
let pendingChunks = [];
let pumpTimer = null;
let isPlaying = false;
let discardIncomingAudio = false; // Drop late in-flight PCM after a retract until the next audio_start
// Said once per voice session, not once per turn (see 'speech_unavailable').
let speechUnavailableAnnounced = false;
let actualTtsSampleRate = TTS_SAMPLE_RATE;  // Updated from server

let voiceAvailable = false;       // Whether anything can HEAR (stt) right now

/**
 * The microphone: live, dead with a reason, or (no reason known) absent.
 *
 * It used to vanish whenever nothing could hear. With transcription switched
 * off on every worker and the workers plainly visible on the board, a missing
 * mic read as a broken page. Present but dead, saying which switch made it
 * so, is the rule read-aloud already follows.
 */
function showMicState(btn, available, reason) {
    if (available) {
        btn.style.display = '';
        btn.disabled = false;
        btn.classList.remove('unavailable');
        btn.title = btn.dataset.liveTitle || btn.title || '';
        return;
    }
    if (!reason) {
        btn.style.display = 'none';
        return;
    }
    if (!btn.dataset.liveTitle) btn.dataset.liveTitle = btn.title || '';
    btn.style.display = '';
    btn.disabled = true;
    btn.classList.add('unavailable');
    btn.title = reason;
}
let voiceDepsMissing = false;     // True only when local packages are absent
let voiceBtnClickWired = false;   // addEventListener exactly once across re-checks
let modelsReady = false;          // Whether voice models are downloaded
let isMuted = false;              // Whether mic is muted

let lastTranscript = '';          // Last transcript submitted (for barge-in concatenation)
let bargeInTranscript = '';       // Accumulated transcript during barge-in
let sendMessagePromise = null;    // Tracks in-flight sendMessage() for barge-in cleanup
let cancelledPromise = null;      // Saved promise from cancelled sendMessage()

let sfxContext = null;            // AudioContext for sound effects
let sfxBuffers = {};              // { bell: AudioBuffer, search: AudioBuffer }
let toolCallCount = 0;            // Progressive pitch counter, resets per exchange
let stuckTimer = null;            // Safety timeout for stuck states

// Progressive TTS state (sentence-level streaming during LLM response)
let ttsRawTail = '';              // The raw text last cut from the stream (lists need it)
let ttsSentenceBuffer = '';       // Accumulates text for sentence boundary detection
let ttsPendingChunk = '';         // Holds short sentences until bundled to minimum length
let ttsSessionActive = false;     // Whether a progressive TTS session is in progress

const MIN_TTS_CHUNK_CHARS = 80;  // Bundle sentences until this many chars before sending

// Jitter buffer for TTS playback. Delegated TTS travels worker → Hetzner →
// client, so PCM frames arrive with network jitter; scheduling the first frame
// (or the first after a drain) with a small lead keeps the gapless schedule
// ahead of "now" so a late frame doesn't underrun into a click/crackle. Local
// (non-delegated) TTS has no hop and comfortably stays ahead regardless.
// 0.8s base: TTS supply runs ~3x real-time, so a deeper opening buffer
// costs one beat of extra startup silence and buys immunity to the
// jitter of cell-hotspot paths (Samsung->GL.iNet->phone->CGNAT->CF).
const PLAYBACK_LEAD_SEC = 0.8;
// Adaptive jitter buffer: when the incoming PCM stream runs marginally
// behind real-time (WAN jitter on a long read), a FIXED lead erodes and the
// scheduler re-anchors over and over — every re-anchor is a discontinuity
// click, and dense clicks are the "static creeping in after a minute" a
// long narration produced. Repeated underruns grow the lead (more silence
// up front, bought once) instead of paying a click per chunk forever.
const PLAYBACK_LEAD_MAX_SEC = 3.0;
// The learned jitter budget, remembered across utterances AND page loads.
// It used to reset to the default every utterance, so a path that genuinely
// needs a deeper buffer re-learned it from scratch every single time, and
// each doubling costs at least one audible underrun. The crackle at the
// start of a read was the buffer relearning something it already knew an
// utterance ago. Persisted per browser, since this is a property of the
// listener's network path rather than of the server.
const PLAYBACK_LEAD_KEY = 'ragsmith_tts_lead_sec';
function loadLearnedLead() {
    const v = parseFloat(localStorage.getItem(PLAYBACK_LEAD_KEY) || '');
    return Number.isFinite(v)
        ? Math.min(Math.max(v, PLAYBACK_LEAD_SEC), PLAYBACK_LEAD_MAX_SEC)
        : PLAYBACK_LEAD_SEC;
}
let playbackLeadSec = loadLearnedLead();
let playbackUnderruns = 0;

// Narrator voice mode state
let narratorVerbatimMode = false;      // True when in verbatim narrator voice mode (no mic)
let narratorAudio = null;              // <audio> MediaElement for narrator TTS. Plays a streaming
                                       // MP3 so the browser's media pipeline (not the JS event loop)
                                       // handles it — keeps playing in background tabs / display off.
let narratorAbortController = null;    // AbortController for the in-flight /voice/tts/prepare fetch
let mediaKeepAliveAudio = null;        // Silent <audio> element to prevent mobile suspend

// Ingest read-aloud playback state — borrows the voice UI for a single
// document playback. No mic, no chunk cycling, just pause/mute/close.
let ingestPlaybackActive = false;
let ingestPlaybackMuted = false;

// Crash recovery / resume state
let intentionalStop = false;           // True only when user (not error) closed voice
let lastNarratorText = '';             // Text of currently-playing narrator chunk (for resume)
let narratorReconnectAttempt = 0;      // Exponential backoff counter for reconnect
let narratorReconnectTimer = null;     // setTimeout id for the next reconnect attempt
let narratorStallDetected = false;     // Stall watchdog flips this; catch-block uses it to reconnect on AbortError
let interChunkGapTimer = null;         // Drops BUFFERING -> LISTENING after gap exceeds budget
let wsReconnectAttempt = 0;            // Backoff counter for WebSocket reconnect
let wsReconnectTimer = null;
const MAX_RECONNECT_ATTEMPTS = 5;
const INTER_CHUNK_GAP_MS = 4500;       // Show "Buffering..." for up to ~4.5s between chunks
const STALL_TIMEOUT_MS = 5000;         // No data + empty buffer for this long = stalled connection

// True from audio_start until the first PCM chunk arrives (or the TTS attempt
// ends). While set, the UI shows "Buffering..." — "Speaking..." is reserved
// for audio that is actually playing, which matters on slow GPU workers where
// the first chunk can take 15s+.
let voiceTtsPending = false;

// True from audio_start until audio_complete: the server-side TTS stream is
// still open. Playback draining while this is set means "waiting for the next
// chunk" (slow worker), NOT "done speaking" — treating a mid-stream drain as
// the end flipped the state to LISTENING and the state guard then discarded
// the whole rest of the utterance (the Predator no-audio bug).
let ttsStreamActive = false;

// The in-chat "Transcribing…" placeholder bubble (shown while Whisper runs,
// replaced by the real user message when the transcript lands).
let transcribingBubble = null;

function showTranscribingBubble() {
    if (transcribingBubble || !elements.chatContainer) return;
    const div = document.createElement('div');
    div.className = 'message user voice-transcribing';
    div.innerHTML = '<div class="voice-transcribing-inner"><span class="spinner"></span><span>Transcribing…</span></div>';
    elements.chatContainer.appendChild(div);
    transcribingBubble = div;
    scrollToBottom();
}

function removeTranscribingBubble() {
    if (transcribingBubble) {
        transcribingBubble.remove();
        transcribingBubble = null;
    }
}

// Dictation mode state
let dictationActive = false;            // True when dictation session is in progress
let dictationBuffer = '';               // Accumulated transcript text across chunks
let dictationMessageEl = null;          // The in-progress message DOM element being appended to

// ─── Initialization ──────────────────────────────────────────────────────────

/**
 * Check voice availability and show/hide the mic button.
 */
/**
 * Re-ask the server what voice can do and re-apply the mic's visibility.
 *
 * Availability used to be checked exactly once, at page load — so flipping
 * a cloud lane (or parking a worker half) on the Switchboard changed
 * nothing in tabs that were already open, which read as "the lane is on
 * but there's no playback". Runs again whenever the tab regains focus:
 * that is the moment an operator comes back from the board.
 */
/** The TTS voice selector in Settings follows SPEECH, not the microphone.
 *
 *  It used to be revealed inside `if (voiceAvailable)`, and that flag is the
 *  transcription question. A pool that could speak but not listen (one worker
 *  switched off, transcription parked on the other) therefore hid every
 *  speech setting while the server was reporting tts_available: true with
 *  twenty-nine voices on offer. It was also decided once, at startup, so
 *  switching a worker on at the board never brought it back.
 */
function showTtsVoiceSection() {
    const section = document.getElementById('tts-voice-section');
    if (section) section.style.display = state.ttsAvailable === false ? 'none' : '';
}

export async function refreshVoiceAvailability() {
    const voiceBtn = document.getElementById('voice-btn');
    try {
        const res = await fetch(`${API_BASE}/voice/status`, { headers: getHeaders(false) });
        if (!res.ok) return;
        const data = await res.json();
        voiceAvailable = data.stt_available ?? data.available;
        voiceDepsMissing = Array.isArray(data.missing) && data.missing.length > 0;
        state.voiceAvailable = data.stt_available ?? data.available;
        state.ttsAvailable = data.tts_available ?? data.available;
        showTtsVoiceSection();
        document.dispatchEvent(new CustomEvent('voice-availability-changed'));
        if (voiceBtn) {
            showMicState(voiceBtn, voiceAvailable, data.stt_reason);
            if (voiceAvailable && !voiceBtnClickWired) {
                voiceBtn.addEventListener('click', onVoiceBtnClick);
                voiceBtnClickWired = true;
            }
        }
    } catch { /* transient — keep the current belief */ }
}

export async function initVoice() {
    window.addEventListener('focus', () => {
        // Never mid-session: a voice session already knows its own state,
        // and yanking the button out from under it would be worse.
        if (voiceState === VOICE_STATES.IDLE) refreshVoiceAvailability();
    });
    const voiceBtn = document.getElementById('voice-btn');
    if (!voiceBtn) return;

    try {
        const res = await fetch(`${API_BASE}/voice/status`, { headers: getHeaders(false) });
        if (!res.ok) {
            voiceBtn.style.display = 'none';
            return;
        }
        const data = await res.json();
        // The mic is the STT question. `available` is kept for old servers,
        // but on a current one it ALSO means stt — so a box that can only
        // speak must not show a mic, and a box that can only hear must.
        voiceAvailable = data.stt_available ?? data.available;
        voiceDepsMissing = Array.isArray(data.missing) && data.missing.length > 0;
        modelsReady = data.models_ready;
        // Share it: the TTS buttons and the dictation entry live in other
        // modules and must not offer speech the pool cannot deliver.
        state.voiceAvailable = data.stt_available ?? data.available;
        // Older servers answer with one flag; treat it as covering both.
        state.ttsAvailable = data.tts_available ?? data.available;
        document.dispatchEvent(new CustomEvent('voice-availability-changed'));

        showMicState(voiceBtn, voiceAvailable, data.stt_reason);
        state.sttReason = data.stt_reason || null;
        if (voiceAvailable) {
            voiceBtn.addEventListener('click', onVoiceBtnClick);
            voiceBtnClickWired = true;
        }
        showTtsVoiceSection();
    } catch (e) {
        // Server might not support voice yet
        voiceBtn.style.display = 'none';
    }
}

/**
 * Update voice button icon based on mode.
 * Dictation mode (highest priority): person-speaking icon only.
 * Verbatim narrator: headphones only.
 * Non-verbatim narrator: headphones + mic.
 * Normal: mic only.
 */
export function updateVoiceBtnIcon(narratorMode, isVerbatim) {
    const btn = document.getElementById('voice-btn');
    const micIcon = btn?.querySelector('.mic-icon');
    const hpIcon = btn?.querySelector('.headphones-icon');
    const dictationIcon = btn?.querySelector('.dictation-icon');
    if (!btn || !micIcon || !hpIcon) return;

    // Dictation takes precedence over narrator mode for icon swap
    if (state.dictationMode) {
        micIcon.style.display = 'none';
        hpIcon.style.display = 'none';
        if (dictationIcon) dictationIcon.style.display = '';
        btn.title = 'Start dictation';
        return;
    }
    if (dictationIcon) dictationIcon.style.display = 'none';

    if (narratorMode && isVerbatim) {
        micIcon.style.display = 'none';
        hpIcon.style.display = '';
        btn.title = 'Listen to narration';
    } else if (narratorMode) {
        micIcon.style.display = '';
        hpIcon.style.display = '';
        btn.title = 'Voice narration';
    } else {
        micIcon.style.display = '';
        hpIcon.style.display = 'none';
        btn.title = 'Voice chat';
    }
}

// ─── Voice Button Handler ────────────────────────────────────────────────────

async function onVoiceBtnClick() {
    if (voiceState !== VOICE_STATES.IDLE) {
        stopVoiceMode();
        return;
    }

    // Autoplay unlock: "bless" the narrator <audio> element inside this user
    // gesture by briefly playing a silent clip, so later auto-plays (which fire
    // after an LLM response, with no fresh gesture) aren't blocked by the
    // browser's autoplay policy.
    try {
        const a = getNarratorAudio();
        a.muted = true;
        a.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAESsAAABAAgAZGF0YQAAAAA=';
        a.play().then(() => a.pause()).catch(() => {});
    } catch (_) {}

    // Dictation mode: skip model check (no LLM involved) and route to dictation flow
    if (state.dictationMode) {
        if (!voiceAvailable) {
            addMessage('system', voiceDepsMissing
                ? 'Voice dependencies not installed. Run: pip install -e ".[voice]"'
                : 'Transcription needs a GPU worker or the cloud transcription lane — nothing is switched on right now.');
            return;
        }
        await startDictationMode();
        return;
    }

    // Check if model is loaded (verbatim narrator doesn't need one)
    const isVerbatim = state.narratorMode && !state.narratorPaused && state.agentSettings?.narrator_verbatim;
    if (!state.loadedModel && !isVerbatim) {
        addMessage('system', 'Load a model before starting voice chat.');
        return;
    }

    if (!voiceAvailable) {
        addMessage('system', voiceDepsMissing
            ? 'Voice dependencies not installed. Run: pip install -e ".[voice]"'
            : 'Transcription needs a GPU worker or the cloud transcription lane — nothing is switched on right now.');
        return;
    }

    // Auto-load models if not ready (no modal — just show connecting state)
    if (!modelsReady) {
        setState(VOICE_STATES.CONNECTING);
        showVoiceUI();
        try {
            const res = await fetch(`${API_BASE}/voice/models/download`, { headers: getHeaders(false) });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
            }
            modelsReady = true;
        } catch (e) {
            hideVoiceUI();
            setState(VOICE_STATES.IDLE);
            addMessage('system', `Voice model loading failed: ${e.message}`);
            return;
        }
    }

    if (isVerbatim) {
        startVerbatimNarratorMode();
    } else {
        await startVoiceMode();
    }
}

// ─── Start/Stop Voice Mode ───────────────────────────────────────────────────

async function startVoiceMode() {
    speechUnavailableAnnounced = false;  // fresh session, say it once again
    intentionalStop = false;
    narratorReconnectAttempt = 0;
    wsReconnectAttempt = 0;
    setState(VOICE_STATES.CONNECTING);

    // Check secure context (getUserMedia requires HTTPS or localhost)
    if (!window.isSecureContext || !navigator.mediaDevices) {
        setState(VOICE_STATES.IDLE);
        const flagsUrl = 'chrome://flags/#unsafely-treat-insecure-origin-as-secure';
        addMessage('system', `Voice chat requires a secure connection (HTTPS or localhost). If accessing over LAN, paste this into your address bar and add this origin: ${flagsUrl}`);
        const lastMsg = elements.chatBox.lastElementChild;
        if (lastMsg) {
            const copyBtn = document.createElement('button');
            copyBtn.textContent = 'Copy URL';
            copyBtn.className = 'modal-btn primary-btn';
            copyBtn.style.cssText = 'margin: 0.5rem auto; display: block; font-size: 0.75rem; padding: 0.25rem 0.75rem;';
            copyBtn.onclick = () => {
                navigator.clipboard.writeText(flagsUrl).then(() => {
                    copyBtn.textContent = 'Copied!';
                    setTimeout(() => { copyBtn.textContent = 'Copy URL'; }, 2000);
                });
            };
            lastMsg.appendChild(copyBtn);
        }
        return;
    }

    try {
        // Request microphone permission
        mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                sampleRate: INPUT_SAMPLE_RATE,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            }
        });
    } catch (e) {
        console.error('[Voice] Mic permission denied:', e);
        setState(VOICE_STATES.IDLE);
        addMessage('system', 'Microphone access denied. Please allow microphone access for voice chat.');
        return;
    }

    try {
        // Set up AudioContext + AudioWorklet for capture
        audioContext = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE });

        // Register the worklet processor
        await audioContext.audioWorklet.addModule('js/voice-processor.js');

        sourceNode = audioContext.createMediaStreamSource(mediaStream);
        workletNode = new AudioWorkletNode(audioContext, 'voice-processor');

        // Handle PCM data from worklet
        workletNode.port.onmessage = (e) => {
            if (!isMuted && ws && ws.readyState === WebSocket.OPEN && voiceState !== VOICE_STATES.IDLE && voiceState !== VOICE_STATES.CONNECTING) {
                const b64 = int16ToBase64(e.data);
                ws.send(JSON.stringify({ type: 'audio', data: b64 }));
            }
        };

        sourceNode.connect(workletNode);
        workletNode.connect(audioContext.destination);

        // Open WebSocket (audio I/O only — no chat logic)
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${window.location.host}/voice/stream${voiceSocketAuth('?')}`;
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
            ws.send(JSON.stringify({ type: 'start' }));
        };

        ws.binaryType = 'arraybuffer';
        ws.onmessage = (e) => {
            if (e.data instanceof ArrayBuffer) {
                // Binary frame: raw PCM float32 audio
                handleBinaryAudio(e.data);
            } else if (e.data instanceof Blob) {
                console.warn('[VoiceDbg] binary arrived as Blob (binaryType lost?)');
            } else {
                handleServerMessage(JSON.parse(e.data));
            }
        };

        ws.onclose = () => {
            // If the user didn't intentionally stop and we're still in a voice
            // session, treat this as a dropped connection and try to reconnect.
            if (intentionalStop || voiceState === VOICE_STATES.IDLE) return;
            // Kill orphaned playback: TTS synthesizes ~3x faster than
            // real-time, so MINUTES of audio can already be scheduled in the
            // AudioContext. Letting it play against a dead session means
            // sound with no Speaking indicator, no barge-in, and — after a
            // reconnect — two sessions' audio overlapping.
            cleanupPlayback();
            scheduleWsReconnect();
        };

        ws.onerror = (e) => {
            console.error('[Voice] WebSocket error:', e);
            // Let onclose drive the recovery — multiple firing causes double-schedule.
        };

        // Show voice mode UI
        showVoiceUI();

    } catch (e) {
        console.error('[Voice] Failed to start:', e);
        cleanupAudio();
        setState(VOICE_STATES.IDLE);
    }
}

/**
 * Start verbatim narrator mode — no mic, no WebSocket.
 * Pure TTS playback mode with Next/Prev navigation.
 */
function startVerbatimNarratorMode() {
    narratorVerbatimMode = true;
    intentionalStop = false;
    narratorReconnectAttempt = 0;
    showVoiceUI();
    setState(VOICE_STATES.LISTENING);  // Ring shows blue "ready" state
    const statusText = document.querySelector('.voice-status-text');
    if (statusText) statusText.textContent = 'Ready';
    // Scroll to the last narrator response
    scrollToLastNarratorMessage();
}

/**
 * Start dictation mode — Whisper transcribes speech, no LLM involvement.
 * Reuses the voice WebSocket with mode=dictation. Server sends back
 * 'dictation_chunk' messages instead of triggering chat.
 */
async function startDictationMode() {
    setState(VOICE_STATES.CONNECTING);

    if (!window.isSecureContext || !navigator.mediaDevices) {
        setState(VOICE_STATES.IDLE);
        addMessage('system', 'Dictation requires a secure connection (HTTPS or localhost).');
        return;
    }

    try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                sampleRate: INPUT_SAMPLE_RATE,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            }
        });
    } catch (e) {
        console.error('[Dictation] Mic permission denied:', e);
        setState(VOICE_STATES.IDLE);
        addMessage('system', 'Microphone access denied. Please allow microphone access for dictation.');
        return;
    }

    try {
        audioContext = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE });
        await audioContext.audioWorklet.addModule('js/voice-processor.js');
        sourceNode = audioContext.createMediaStreamSource(mediaStream);
        workletNode = new AudioWorkletNode(audioContext, 'voice-processor');
        workletNode.port.onmessage = (e) => {
            if (!isMuted && ws && ws.readyState === WebSocket.OPEN
                && voiceState !== VOICE_STATES.IDLE && voiceState !== VOICE_STATES.CONNECTING) {
                const b64 = int16ToBase64(e.data);
                ws.send(JSON.stringify({ type: 'audio', data: b64 }));
            }
        };
        sourceNode.connect(workletNode);
        workletNode.connect(audioContext.destination);

        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${window.location.host}/voice/stream?mode=dictation${voiceSocketAuth('&')}`;
        ws = new WebSocket(wsUrl);
        ws.onopen = () => ws.send(JSON.stringify({ type: 'start' }));
        ws.binaryType = 'arraybuffer';
        ws.onmessage = (e) => {
            if (typeof e.data === 'string') handleServerMessage(JSON.parse(e.data));
        };
        ws.onclose = () => {
            if (voiceState !== VOICE_STATES.IDLE) stopDictationMode();
        };
        ws.onerror = (e) => {
            console.error('[Dictation] WebSocket error:', e);
            stopDictationMode();
        };

        dictationActive = true;
        dictationBuffer = '';
        dictationMessageEl = null;
        showVoiceUI();
    } catch (e) {
        console.error('[Dictation] Failed to start:', e);
        cleanupAudio();
        setState(VOICE_STATES.IDLE);
    }
}

/**
 * Append transcript text to the in-progress dictation message.
 * Creates the message element on first chunk; appends to it on subsequent chunks.
 */
function appendDictationText(text) {
    if (!text || !text.trim()) return;
    const trimmed = text.trim();
    dictationBuffer = dictationBuffer ? `${dictationBuffer} ${trimmed}` : trimmed;

    if (!dictationMessageEl) {
        // Create a placeholder dictation message (no message_id yet — that comes after save)
        dictationMessageEl = addMessage('user', dictationBuffer, { isDictation: true, inProgress: true });
    } else {
        // Update existing message content
        const contentEl = dictationMessageEl.querySelector('.message-content');
        if (contentEl) contentEl.textContent = dictationBuffer;
    }
    dictationMessageEl?.scrollIntoView({ behavior: 'smooth', block: 'end' });
}

/**
 * Stop dictation mode — close WS, save the accumulated transcript via API,
 * then upgrade the in-progress message DOM with the real message_id + footer.
 */
export async function stopDictationMode() {
    voiceGeneration++;

    // Send stop + close WebSocket
    if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'stop' })); } catch (_) {}
    }
    cleanupAudio();
    if (ws) {
        ws.onmessage = null;
        ws.onclose = null;
        ws.close();
        ws = null;
    }

    hideVoiceUI();
    setState(VOICE_STATES.IDLE);

    const text = dictationBuffer.trim();
    const msgEl = dictationMessageEl;
    dictationActive = false;
    dictationBuffer = '';
    dictationMessageEl = null;

    if (!text) {
        // Nothing was captured — remove the placeholder if any
        if (msgEl) msgEl.remove();
        return;
    }

    // Save to server. Creates a session if none exists.
    try {
        const headers = getHeaders(true);
        const body = {
            text,
            session_id: state.sessionId || null,
            agent: state.loadedAgent || null,
            collection: state.loadedCollection || null,
            extract_facts: !!state.dictationExtractFacts,
            model: state.loadedModel || null,
        };
        const res = await fetch(`${API_BASE}/dictation`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`save failed (${res.status})`);
        const data = await res.json();

        // If a new session was created, update state
        if (data.session_id && !state.sessionId) {
            state.sessionId = data.session_id;
        }

        // Upgrade the placeholder with real message_id + finalize footer
        if (msgEl && data.message_id) {
            // Remove the placeholder; addMessage will append the new one
            const parent = msgEl.parentNode;
            const next = msgEl.nextSibling;
            msgEl.remove();
            const replacement = addMessage('user', text, {
                isDictation: true,
                messageId: data.message_id,
                extracting: !!data.extracting,
            });
            // If we needed to preserve order (e.g. user added more messages
            // while save was in flight), reposition into the original spot
            if (replacement && parent && next && replacement.parentNode === parent
                && next !== replacement) {
                parent.insertBefore(replacement, next);
            }

            // If extraction was scheduled, poll for completion (mirror chat flow)
            if (data.extracting && data.session_id) {
                pollDictationExtractionStatus(data.session_id, replacement);
            }
        }
    } catch (e) {
        console.error('[Dictation] Save failed:', e);
        addMessage('system', `Dictation save failed: ${e.message}`);
    }
}

/**
 * Poll the extraction status endpoint and update the dictation message
 * footer when extraction completes (mirrors the chat.js pattern).
 */
export async function pollDictationExtractionStatus(sessionId, messageEl, maxAttempts = 30) {
    if (!sessionId || !messageEl) return;
    const { updateExtractionStatus } = await import('./ui.js');
    let attempts = 0;
    const poll = async () => {
        try {
            const res = await fetch(`${API_BASE}/sessions/${sessionId}/extraction_status`, {
                headers: getHeaders(false)
            });
            if (!res.ok) return;
            const data = await res.json();
            if (!data.extracting) {
                // Done — remove spinner from the message footer
                const status = messageEl.querySelector('.extraction-status');
                if (status) status.remove();
                return;
            }
            attempts++;
            if (attempts < maxAttempts) {
                setTimeout(poll, 1000);
            } else {
                const status = messageEl.querySelector('.extraction-status');
                if (status) status.remove();
            }
        } catch (_) {}
    };
    setTimeout(poll, 500);
}

/**
 * Update Next/Prev button labels based on current narrator position.
 * - "Begin" + hide Prev when no messages exist yet (chunk 1)
 * - "Next" + show Prev otherwise
 */
export function updateNextButtonLabel() {
    const nextBtn = document.getElementById('voice-narrator-next');
    const prevBtn = document.getElementById('voice-narrator-prev');
    const all = getAllNarratorMessages();
    const chevronRight = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>';

    if (all.length === 0) {
        // No messages yet — show "Begin", hide Prev
        if (nextBtn) { nextBtn.innerHTML = 'Begin ' + chevronRight; nextBtn.style.display = ''; }
        if (prevBtn) prevBtn.style.display = 'none';
    } else {
        // Check if the last narrator message covers the final chunk
        // total_chunks = MAX(id) from DB, so chunk_id comparison works
        // even with non-sequential IDs
        const last = all[all.length - 1];
        const chunkId = parseInt(last.dataset.narratorChunkId) || 0;
        const chunkCount = parseInt(last.dataset.narratorChunkCount) || 1;
        const totalChunks = parseInt(last.dataset.narratorTotalChunks) || 0;
        const isAtEnd = totalChunks > 0 && (chunkId + chunkCount - 1) >= totalChunks;
        const cur = currentNarratorMessage;
        const curIsLast = !cur || cur === last || all.indexOf(cur) === all.length - 1;

        if (isAtEnd && curIsLast) {
            // At the very end of the book — hide Next
            if (nextBtn) nextBtn.style.display = 'none';
        } else {
            if (nextBtn) { nextBtn.innerHTML = 'Next ' + chevronRight; nextBtn.style.display = ''; }
        }
        // Hide Prev when on the first message
        const curIdx = cur ? all.indexOf(cur) : all.length - 1;
        if (prevBtn) prevBtn.style.display = curIdx <= 0 ? 'none' : '';
    }
}

/**
 * Scroll to the last narrator assistant message and highlight it.
 */
function scrollToLastNarratorMessage() {
    const all = getAllNarratorMessages();
    if (all.length > 0) {
        currentNarratorMessage = all[all.length - 1];
        syncNarratorStateFromElement(currentNarratorMessage);
    }
    clearNarratorHighlight();
    elements.chatContainer.scrollTo({ top: elements.chatContainer.scrollHeight, behavior: 'smooth' });
}

export function stopVoiceMode() {
    // Dictation mode has its own cleanup + save flow
    if (dictationActive) {
        stopDictationMode();
        return;
    }

    intentionalStop = true;
    cancelPendingNarratorReconnect();
    if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null; }
    narratorReconnectAttempt = 0;
    wsReconnectAttempt = 0;
    lastNarratorText = '';
    clearInterChunkGap();

    voiceGeneration++;

    // Clear narrator highlighting on exit
    clearNarratorHighlight();
    currentNarratorMessage = null;

    // Verbatim narrator: no WebSocket/mic to clean up
    if (narratorVerbatimMode) {
        stopNarratorTts();
        narratorVerbatimMode = false;
        hideVoiceUI();
        setState(VOICE_STATES.IDLE);
        return;
    }

    // Send stop message
    if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'stop' })); } catch (e) {}
    }

    cleanupAudio();
    cleanupPlayback();
    stopNarratorTts();

    // Close WebSocket
    if (ws) {
        ws.onmessage = null;  // Prevent late binary frames from triggering audio
        ws.onclose = null;    // Prevent recursive stop
        ws.close();
        ws = null;
    }

    lastTranscript = '';
    bargeInTranscript = '';
    sendMessagePromise = null;
    cancelledPromise = null;
    toolCallCount = 0;
    ttsSentenceBuffer = '';
    ttsRawTail = '';
    ttsPendingChunk = '';
    ttsSessionActive = false;
    voiceTtsPending = false;
    ttsStreamActive = false;
    removeTranscribingBubble();
    if (stuckTimer) { clearTimeout(stuckTimer); stuckTimer = null; }
    hideVoiceUI();
    setState(VOICE_STATES.IDLE);
}

// ─── Server Message Handler ──────────────────────────────────────────────────

function handleServerMessage(msg) {
    switch (msg.type) {
        case 'ready':
            break;

        case 'dictation_chunk':
            // Whisper transcript while in dictation mode — append to message
            appendDictationText(msg.text || '');
            setState(VOICE_STATES.LISTENING);
            return;

        case 'listening':
            // A noise/no-speech utterance also ends here — drop the
            // transcribing placeholder so it doesn't linger.
            removeTranscribingBubble();
            // Barge-in: stop TTS playback if assistant was speaking
            if (voiceState === VOICE_STATES.ASSISTANT_SPEAKING) {
                cleanupPlayback();
            }
            // Clean up false barge-in: if we had a pending barge-in cancel
            // but got listening (empty transcript from noise) instead of a
            // real transcript, undo the cancelled exchange so it's visible again
            if (bargeInTranscript) {
                bargeInTranscript = '';
                const waitFor = cancelledPromise || Promise.resolve();
                cancelledPromise = null;
                waitFor.finally(() => {
                    _removeCancelledClass();
                });
            }
            if (voiceTtsPending || ttsStreamActive) {
                // TTS was cancelled/ended before finishing (e.g. barge-in
                // while buffering) — honor the transition.
                voiceTtsPending = false;
                ttsStreamActive = false;
                cleanupPlayback();
                setState(VOICE_STATES.LISTENING);
            } else if (voiceState !== VOICE_STATES.BUFFERING) {
                // Don't override narrator inter-chunk BUFFERING
                setState(VOICE_STATES.LISTENING);
            }
            break;

        case 'transcribing':
            // Whisper started on the utterance. Dictation renders its own
            // in-progress message, so only decorate normal voice mode.
            if (!dictationActive) {
                setState(VOICE_STATES.TRANSCRIBING);
                showTranscribingBubble();
            }
            break;

        case 'speech_start':
            // Barge-in during processing: cancel chat, save transcript and promise
            if (voiceState === VOICE_STATES.PROCESSING) {
                cancelChat().catch(() => {});
                bargeInTranscript = lastTranscript;
                cancelledPromise = sendMessagePromise;
                // Mark the user message as cancelled — CSS hides it + everything after it
                _markCancelledExchange();
            }
            setState(VOICE_STATES.USER_SPEAKING);
            break;

        case 'transcript':
            removeTranscribingBubble();
            // STT transcript ready — concatenate with any barge-in context
            if (msg.text) {
                let fullTranscript = msg.text;
                if (bargeInTranscript) {
                    fullTranscript = bargeInTranscript + ' ' + msg.text;
                    bargeInTranscript = '';
                    // Wait for cancelled sendMessage() to fully finish, then clean up and resubmit
                    const waitFor = cancelledPromise || Promise.resolve();
                    cancelledPromise = null;
                    waitFor.finally(() => {
                        _removeCancelledExchange();
                        submitTranscript(fullTranscript);
                    });
                } else {
                    submitTranscript(fullTranscript);
                }
            }
            break;

        case 'audio_start':
            // Synthesis has STARTED, not audio — show "Buffering..." until the
            // first PCM chunk actually arrives (handleAudioChunk flips to
            // ASSISTANT_SPEAKING). On slow GPU workers this gap can be 15s+;
            // claiming "Speaking..." for it was a lie that read as a failure.
            voiceTtsPending = true;
            ttsStreamActive = true;
            discardIncomingAudio = false;  // Fresh session — accept its frames
            setState(VOICE_STATES.BUFFERING);
            if (msg.sample_rate) {
                actualTtsSampleRate = msg.sample_rate;
            }
            break;

        case 'speech_unavailable':
            // Voice without a voice: transcription still works, so the
            // session stays OPEN and listening. The person keeps talking and
            // reads the replies; they close it themselves when done. Said
            // once per session — repeating it every turn would be nagging.
            if (!speechUnavailableAnnounced) {
                speechUnavailableAnnounced = true;
                addMessage('system', msg.reason
                    || 'No voice worker is switched on, so replies will be text only. Keep talking — I am still listening.');
            }
            voiceTtsPending = false;
            ttsStreamActive = false;
            setState(VOICE_STATES.LISTENING);
            smoothScrollToBottom();
            break;

        case 'audio_complete':
            if (voiceTtsPending && !isPlaying) {
                // Stream ended without a single audio chunk — synthesis
                // produced nothing. Say so instead of failing silently.
                console.warn('[Voice] TTS stream completed with no audio');
                addMessage('system', 'Speech synthesis produced no audio — the compute worker may be overloaded.');
                scrollToBottom();
            }
            voiceTtsPending = false;
            ttsStreamActive = false;
            // TTS done — transition to listening when playback finishes
            // (if audio is still playing, the final source.onended handles it)
            if (!isPlaying) {
                setState(VOICE_STATES.LISTENING);
            }
            break;

        case 'voice_failed':
            // Too many consecutive noise failures — show message, reset to listening
            addMessage('system', msg.message || 'Voice input failed due to background noise.');
            scrollToBottom();
            // Clear any stale barge-in state
            bargeInTranscript = '';
            cancelledPromise = null;
            setState(VOICE_STATES.LISTENING);
            break;

        case 'error':
            console.error('[Voice] Server error:', msg.message);
            addMessage('system', `Voice error: ${msg.message}`);
            scrollToBottom();
            break;
    }
}

// ─── Chat Integration ────────────────────────────────────────────────────────

/**
 * Submit voice transcript through the normal chat pipeline.
 * This reuses sendMessage() so all features work: tools, web search,
 * agent/collection logic, spinner, citations, action bar, etc.
 */
async function submitTranscript(transcript) {
    setState(VOICE_STATES.PROCESSING);
    lastTranscript = transcript;
    toolCallCount = 0;  // Reset progressive pitch for new exchange
    removeTranscribingBubble();

    // Pass the transcript straight to sendMessage() — full chat pipeline
    // (tools, web search, KB, spinner) without the text ever flashing through
    // the input box.
    sendMessagePromise = sendMessage(transcript);

    // The user bubble is created inside sendMessage; tag the fresh one so it
    // fades in instead of popping (replacing the transcribing placeholder).
    setTimeout(() => {
        const userMsgs = elements.chatContainer
            ? elements.chatContainer.querySelectorAll('.message.user:not(.voice-transcribing)')
            : [];
        const last = userMsgs[userMsgs.length - 1];
        if (last) last.classList.add('voice-fade-in');
    }, 60);

    await sendMessagePromise;
    sendMessagePromise = null;

    // TTS will be triggered by onVoiceResponseComplete (called from chat.js onComplete)
}

// ─── Progressive TTS Helpers ────────────────────────────────────────────────

/**
 * Strip markdown formatting from text for TTS consumption.
 * JS port of Python _clean_text_for_tts() in voice.py.
 */
/**
 * Put two pieces of speech text together without flattening the break between
 * them: a blank line here is a pause the server will honour, and joining with
 * a space would silently delete it.
 */
function joinKeepingBreaks(head, tail) {
    const a = (head || '').replace(/[ \t]+$/, '');
    const b = (tail || '').replace(/^[ \t]+/, '');
    if (!a.trim()) return b.trim() ? b.replace(/^\n+/, '') : '';
    if (!b.trim()) return a;
    const joiner = /\n$/.test(a) || /^\n/.test(b) ? '' : ' ';
    return a + joiner + b;
}

// A list item: a bullet (- * +) or a number (1. or 1)), then its text.
const LIST_ITEM = /^[ \t]*(?:[-*+]|\d+[.)])[ \t]+(\S.*?)[ \t]*$/;
const ITEM_ENDED = /[.!?:;\u2026]["')\]\u201d\u2019]*$/;

/**
 * Markdown list items as things a voice can hear as separate. Stripping the
 * markers alone left items with no punctuation, read as one run-on sentence.
 * `before` is the text just ahead of this piece, when it was cut from a
 * stream, so a list starting at the cut still gets its breath.
 */
export function speakLists(text, before = '') {
    const out = [];
    const prev = (before || '').replace(/\s+$/, '').split('\n').pop() || '';
    let inList = LIST_ITEM.test(prev);
    let lastText = prev.trim() !== '';
    for (const line of text.split('\n')) {
        const m = line.match(LIST_ITEM);
        if (!m) {
            if (line.trim()) { inList = false; lastText = true; }
            out.push(line);
            continue;
        }
        let item = m[1];
        if (!ITEM_ENDED.test(item)) item += '.';
        const prevLine = out.length ? out[out.length - 1] : (lastText ? 'x' : '');
        if (!inList && prevLine.trim()) out.push('');
        inList = true;
        out.push(item);
    }
    return out.join('\n');
}

function cleanTextForTts(text, opts = {}) {
    // Strip <think>...</think> blocks first — model thinking should
    // never be read aloud. Handles both complete and unclosed blocks
    // (the latter shouldn't happen but be defensive).
    text = text.replace(/<think>[\s\S]*?<\/think>/g, '');
    text = text.replace(/<think>[\s\S]*$/g, '');
    // Remove fenced code blocks (``` ... ```)
    text = text.replace(/```[\s\S]*?```/g, '');
    // Remove inline code
    text = text.replace(/`([^`]*)`/g, '$1');
    // Remove images ![alt](url)
    text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
    // Convert links [text](url) → text
    text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
    // Remove bare URLs
    text = text.replace(/https?:\/\/\S+/g, '');
    text = text.replace(/www\.\S+/g, '');
    // Remove bold/italic (order matters: *** before ** before *)
    text = text.replace(/\*\*\*(.+?)\*\*\*/g, '$1');
    text = text.replace(/\*\*(.+?)\*\*/g, '$1');
    text = text.replace(/\*(.+?)\*/g, '$1');
    // Remove heading markers
    text = text.replace(/^#{1,6}\s+/gm, '');
    // Remove blockquote markers
    text = text.replace(/^>\s?/gm, '');
    // Remove horizontal rules
    text = text.replace(/^[-*_]{3,}\s*$/gm, '');
    // List items: markers off, each item ended like a sentence, a breath
    // before the list. Same rule as speech_pauses.speak_lists on the server.
    text = speakLists(text, opts.before || '');
    // Remove any remaining asterisks (leftover markdown formatting)
    text = text.replace(/\*+/g, '');
    // Line breaks are KEPT. They used to be turned into full stops here, which
    // is the only pacing a speech engine reads — but it also put a sentence
    // ending where the writer wrote a paragraph break, so the voice dropped
    // its pitch mid-thought and never actually waited. The server turns these
    // runs into real silence instead (see core/speech_pauses.py), so what has
    // to survive the trip is the shape of the text.
    text = text.replace(/\n{5,}/g, '\n\n\n\n');
    text = text.replace(/[ \t]{2,}/g, ' ');
    text = text.replace(/[ \t]+\n/g, '\n');
    text = text.replace(/\.{3,}/g, '.');
    // A piece cut from a stream keeps the breaks at its edges: they are the
    // pause between it and the next piece.
    return opts.keepEdges ? text.replace(/^[ \t]+|[ \t]+$/g, '') : text.trim();
}

/**
 * Find the index of the last sentence boundary (. ! ?) followed by whitespace or end.
 * Avoids splitting on decimal numbers (3.14) and common abbreviations (Dr. Mr. etc.).
 * Returns -1 if no boundary found.
 */
function findSentenceBoundary(text) {
    const regex = /[.!?\n](?=\s|$)/g;
    let lastMatch = -1;
    let m;
    while ((m = regex.exec(text)) !== null) {
        if (text[m.index] === '.') {
            // Skip decimal numbers: digit before and after dot
            const before = m.index > 0 ? text[m.index - 1] : '';
            const after = text[m.index + 1] || '';
            if (/\d/.test(before) && /\d/.test(after)) continue;
            // Skip common abbreviations: 1-2 capital letters before dot
            const prev3 = text.slice(Math.max(0, m.index - 3), m.index);
            if (/(?:^|\s)[A-Z]{1,2}$/.test(prev3)) continue;
        }
        lastMatch = m.index;
    }
    return lastMatch;
}

/**
 * Find the first sentence boundary (. ! ? \n) in text.
 * Returns index of the punctuation, or -1 if none found.
 */
function findFirstSentenceBoundary(text) {
    const regex = /[.!?\n](?=\s|$)/g;
    let m;
    while ((m = regex.exec(text)) !== null) {
        if (text[m.index] === '.') {
            const before = m.index > 0 ? text[m.index - 1] : '';
            const after = text[m.index + 1] || '';
            if (/\d/.test(before) && /\d/.test(after)) continue;
            const prev3 = text.slice(Math.max(0, m.index - 3), m.index);
            if (/(?:^|\s)[A-Z]{1,2}$/.test(prev3)) continue;
        }
        return m.index;
    }
    return -1;
}

/**
 * Split cleaned TTS text into segments at sentence boundaries.
 * Each segment is bundled to at least MIN_TTS_CHUNK_CHARS for natural pacing.
 * Returns array of { text, startOffset, endOffset } relative to the input.
 */
export function splitIntoTtsSegments(cleanText) {
    const segments = [];
    let pos = 0;
    let pending = '';
    let pendingStart = 0;

    while (pos < cleanText.length) {
        const remaining = cleanText.slice(pos);
        const boundary = findFirstSentenceBoundary(remaining);

        if (boundary === -1) {
            // No more boundaries — add remainder to pending
            pending += remaining;
            pos = cleanText.length;
        } else {
            const sentence = remaining.slice(0, boundary + 1);
            pos += boundary + 1;
            // Skip whitespace after boundary
            while (pos < cleanText.length && cleanText[pos] === ' ') pos++;

            if (!pending) pendingStart = pos - sentence.length;
            pending += sentence;

            // Flush when we have enough text for a natural segment
            if (pending.length >= MIN_TTS_CHUNK_CHARS) {
                segments.push({
                    text: pending.trim(),
                    startOffset: pendingStart,
                    endOffset: pos,
                });
                pending = '';
                pendingStart = pos;
            }
        }
    }

    // Flush remaining text
    if (pending.trim()) {
        if (pending.trim().length < 20 && segments.length > 0) {
            const last = segments[segments.length - 1];
            last.text += ' ' + pending.trim();
            last.endOffset = cleanText.length;
        } else {
            segments.push({
                text: pending.trim(),
                startOffset: pendingStart,
                endOffset: cleanText.length,
            });
        }
    }

    return segments;
}

/**
 * Check if text contains an unclosed fenced code block.
 */
function isInsideCodeBlock(text) {
    const fenceCount = (text.match(/```/g) || []).length;
    return fenceCount % 2 !== 0;
}

/**
 * Called by chat.js onChunk during streaming to progressively feed text to TTS.
 * Accumulates text, detects sentence boundaries, and sends completed sentences
 * to the WebSocket for synthesis while the LLM is still generating.
 */
export function onVoiceStreamChunk(chunk) {
    if (voiceState === VOICE_STATES.IDLE || voiceState === VOICE_STATES.USER_SPEAKING) return;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    ttsSentenceBuffer += chunk;

    // Don't extract sentences from inside code blocks
    if (isInsideCodeBlock(ttsSentenceBuffer)) return;

    const boundary = findSentenceBoundary(ttsSentenceBuffer);
    if (boundary === -1) return;

    // Extract text up to and including the sentence boundary
    const rawSentences = ttsSentenceBuffer.slice(0, boundary + 1);
    ttsSentenceBuffer = ttsSentenceBuffer.slice(boundary + 1);

    // Only spaces are trimmed, never the line breaks: the break between two
    // bundled pieces IS the pause the writer asked for, and trimming it here
    // is how it used to be lost before the text ever reached the engine.
    const cleanSentences = cleanTextForTts(rawSentences, { keepEdges: true, before: ttsRawTail });
    ttsRawTail = rawSentences;
    if (!cleanSentences.trim()) return;

    // Bundle short sentences: accumulate until we reach a natural length
    const joiner = !ttsPendingChunk || /\n$/.test(ttsPendingChunk) || /^\n/.test(cleanSentences) ? '' : ' ';
    ttsPendingChunk += joiner + cleanSentences;
    if (ttsPendingChunk.length < MIN_TTS_CHUNK_CHARS) return;

    // Start progressive TTS session on first chunk
    if (!ttsSessionActive) {
        ttsSessionActive = true;
        ws.send(JSON.stringify({ type: 'synthesize_start', voice: getSelectedVoice(), ...getTTSOptions() }));
    }

    ws.send(JSON.stringify({ type: 'synthesize_chunk', text: ttsPendingChunk }));
    ttsPendingChunk = '';
}

/**
 * Called by chat.js when a stream_retract event arrives — text that the
 * server already streamed turned out to be inter-step reasoning, not the
 * final response. We discard buffered TTS chunks (don't flush them) and
 * end the session so a fresh one starts on the next real chunk.
 *
 * Different from onVoiceToolInterrupt: this DISCARDS the buffer; that
 * one FLUSHES it. We don't want to read reasoning content aloud.
 */
export function onVoiceRetract() {
    ttsSentenceBuffer = '';
    ttsRawTail = '';
    ttsPendingChunk = '';
    if (ttsSessionActive && ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'synthesize_end' }));
    }
    ttsSessionActive = false;
    // Progressive voicing is speculative during a tool loop: if the server
    // retracts text as inter-step reasoning AFTER we've already started
    // synthesizing/playing it, silence it immediately rather than reading the
    // leaked reasoning aloud. Halt playback, drop any PCM still in flight until
    // the next real audio_start, and park in BUFFERING so the guard in
    // handleBinaryAudio doesn't discard the genuine response when it arrives.
    if (isPlaying || voiceTtsPending) {
        discardIncomingAudio = true;
        cleanupPlayback();
        if (voiceState === VOICE_STATES.ASSISTANT_SPEAKING) {
            setState(VOICE_STATES.BUFFERING);
        }
    }
}

/**
 * Called by chat.js when a tool starts executing mid-stream.
 * Flushes and ends any active progressive TTS session so the post-tool
 * response can start a fresh session without state corruption.
 */
export function onVoiceToolInterrupt() {
    if (!ttsSessionActive) return;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    // Flush any buffered text
    const tail = joinKeepingBreaks(ttsPendingChunk, cleanTextForTts(ttsSentenceBuffer));
    if (tail) {
        ws.send(JSON.stringify({ type: 'synthesize_chunk', text: tail }));
    }
    ttsSentenceBuffer = '';
    ttsRawTail = '';
    ttsPendingChunk = '';

    // End the progressive session cleanly
    ws.send(JSON.stringify({ type: 'synthesize_end' }));
    ttsSessionActive = false;
}

/**
 * Called by chat.js when a response completes while voice mode is active.
 * Flushes remaining sentence buffer and ends the progressive TTS session,
 * or falls back to sending full text at once if no progressive session started.
 */
export function onVoiceResponseComplete(fullText) {
    if (voiceState === VOICE_STATES.IDLE) return;
    // Suppress TTS if user barged in during processing
    if (voiceState === VOICE_STATES.USER_SPEAKING) {
        ttsSentenceBuffer = '';
        ttsRawTail = '';
        ttsPendingChunk = '';
        ttsSessionActive = false;
        return;
    }

    // Flush remaining buffer from progressive streaming
    if (ttsSessionActive) {
        // Combine any pending (bundled-but-not-yet-sent) text with the remaining sentence buffer
        const tail = joinKeepingBreaks(ttsPendingChunk, cleanTextForTts(ttsSentenceBuffer));
        if (tail && ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'synthesize_chunk', text: tail }));
        }
        ttsSentenceBuffer = '';
        ttsRawTail = '';
        ttsPendingChunk = '';
        // End progressive session
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'synthesize_end' }));
        }
        ttsSessionActive = false;
        return;
    }

    // Fallback: no progressive chunks were sent (very short response or
    // voice mode started after streaming began). Send full text at once.
    // Clean the text first — full_text from the server includes the
    // <think>...</think> wrapper which we must not read aloud.
    ttsSentenceBuffer = '';
    ttsRawTail = '';
    ttsPendingChunk = '';
    const cleaned = cleanTextForTts(fullText || '').trim();
    if (!cleaned) {
        setState(VOICE_STATES.LISTENING);
        return;
    }
    if (!(ws && ws.readyState === WebSocket.OPEN)) {
        setState(VOICE_STATES.LISTENING);
        return;
    }
    // Stream the confirmed final text progressively rather than in one giant
    // request. Tool-loop responses are voiced here (progressive live voicing is
    // suppressed during tool loops because streamed text may be retracted as
    // reasoning), and those answers can be long — chunking gives fast
    // first-audio and natural pacing.
    ws.send(JSON.stringify({ type: 'synthesize_start', voice: getSelectedVoice(), ...getTTSOptions() }));
    let rest = cleaned;
    let bundle = '';
    while (rest.length) {
        const b = findSentenceBoundary(rest);
        let sentence;
        if (b === -1) {
            sentence = rest;
            rest = '';
        } else {
            sentence = rest.slice(0, b + 1);
            rest = rest.slice(b + 1);
        }
        bundle = joinKeepingBreaks(bundle, sentence);
        if (bundle.length >= MIN_TTS_CHUNK_CHARS || !rest.length) {
            if (bundle.trim()) {
                ws.send(JSON.stringify({ type: 'synthesize_chunk', text: bundle }));
            }
            bundle = '';
        }
    }
    ws.send(JSON.stringify({ type: 'synthesize_end' }));
}

// ─── Audio Playback ──────────────────────────────────────────────────────────

/**
 * Handle binary WebSocket frame containing raw PCM float32 audio.
 */
function handleBinaryAudio(arrayBuffer) {
    if (voiceState === VOICE_STATES.IDLE) return;  // Discard late frames after stop
    if (discardIncomingAudio) return;  // Retracted reasoning — drop its trailing frames
    // Drop in-flight frames that arrive AFTER barge-in halted playback. Without
    // this guard, a stale TTS chunk can re-trigger ASSISTANT_SPEAKING and spin
    // up a brand-new AudioContext, "reviving" the speaker after the user
    // already interrupted. (Mid-stream buffer drains park in BUFFERING, not
    // LISTENING, precisely so this guard doesn't eat the rest of a slow
    // worker's utterance.)
    if (voiceState === VOICE_STATES.LISTENING
        || voiceState === VOICE_STATES.USER_SPEAKING
        || voiceState === VOICE_STATES.PROCESSING) {
        return;
    }
    // First real audio: this is the moment "Speaking..." becomes true.
    voiceTtsPending = false;
    setState(VOICE_STATES.ASSISTANT_SPEAKING);
    const float32 = new Float32Array(arrayBuffer);
    queueAudioChunk(float32);
}

function queueAudioChunk(float32) {
    pendingChunks.push(float32);
    pumpPlayback();
}

/**
 * Move chunks from the pending queue into Web Audio, but never more than
 * MAX_SCHEDULE_AHEAD_SEC beyond the playback clock. Re-arms itself for
 * whenever the next slot opens.
 */
function pumpPlayback() {
    if (pumpTimer) { clearTimeout(pumpTimer); pumpTimer = null; }
    while (pendingChunks.length) {
        const now = playbackContext ? playbackContext.currentTime : 0;
        // Always let the first chunk through: it is what creates the context
        // and re-anchors after an underrun.
        if (playbackContext && playbackTime > now + MAX_SCHEDULE_AHEAD_SEC) break;
        scheduleChunk(pendingChunks.shift());
    }
    if (pendingChunks.length && playbackContext) {
        const waitSec = playbackTime - MAX_SCHEDULE_AHEAD_SEC - playbackContext.currentTime;
        pumpTimer = setTimeout(pumpPlayback, Math.max(20, waitSec * 1000));
    }
}

function scheduleChunk(float32) {
    if (!playbackContext) {
        playbackContext = new AudioContext({ sampleRate: actualTtsSampleRate });
        playbackTime = 0;
    }

    // Create AudioBuffer
    const buffer = playbackContext.createBuffer(1, float32.length, actualTtsSampleRate);
    buffer.getChannelData(0).set(float32);

    // Schedule for gapless playback
    const source = playbackContext.createBufferSource();
    source.buffer = buffer;
    source.connect(playbackContext.destination);

    const now = playbackContext.currentTime;
    let startTime;
    if (playbackTime <= now) {
        // First frame, or the buffer drained (underrun). Start with a jitter
        // lead so subsequent frames stay comfortably ahead of the clock and a
        // late-arriving delegated frame doesn't crackle. The lead costs a small
        // startup/resume delay — silence, which beats static.
        playbackUnderruns++;
        {
            // Every underrun is evidence the buffer is too shallow for this
            // network path — widen immediately and decisively (x2 from the
            // FIRST one). Growing timidly from the second underrun let cell
            // jitter bursts land 5-6 audible crackles before the buffer
            // caught up ("slowed down ~10% with static, then recovered").
            playbackLeadSec = Math.min(playbackLeadSec * 2.0, PLAYBACK_LEAD_MAX_SEC);
            // Diagnostic for the sustained-drain case: if underruns keep
            // firing AT the cap, the supply is genuinely below real-time and
            // the fix belongs server-side — this line is the evidence.
            console.warn(`[voice] underrun #${playbackUnderruns}, lead now ${playbackLeadSec.toFixed(2)}s`
                + (playbackLeadSec >= PLAYBACK_LEAD_MAX_SEC ? ' (AT CAP — supply below real-time)' : ''));
        }
        startTime = now + playbackLeadSec;
        // De-click: a re-anchored chunk starts from silence at an arbitrary
        // waveform phase — ramp the first ~4ms so the discontinuity is
        // inaudible instead of a pop.
        const ch = buffer.getChannelData(0);
        const ramp = Math.min(ch.length, Math.floor(actualTtsSampleRate * 0.004));
        for (let i = 0; i < ramp; i++) ch[i] *= i / ramp;
    } else {
        startTime = playbackTime;
    }
    source.start(startTime);
    playbackTime = startTime + buffer.duration;
    isPlaying = true;

    // Track when playback ends
    source.onended = () => {
        if (pendingChunks.length) return;  // More audio queued, not a drain.
        if (playbackContext && playbackContext.currentTime >= playbackTime - 0.01) {
            isPlaying = false;
            if (voiceState === VOICE_STATES.ASSISTANT_SPEAKING) {
                if (ttsStreamActive) {
                    // Buffer drained but the server's TTS stream is still open —
                    // the worker just hasn't produced the next chunk yet (slow
                    // GPU). Show Buffering and KEEP ACCEPTING frames; flipping
                    // to LISTENING here made the state guard discard the rest
                    // of the utterance.
                    setState(VOICE_STATES.BUFFERING);
                } else {
                    setState(VOICE_STATES.LISTENING);
                }
            }
        }
    };
}

function cleanupPlayback() {
    if (playbackContext) {
        playbackContext.suspend().catch(() => {});  // Immediate silence
        playbackContext.close().catch(() => {});
        playbackContext = null;
    }
    playbackTime = 0;
    isPlaying = false;
    pendingChunks = [];
    if (pumpTimer) { clearTimeout(pumpTimer); pumpTimer = null; }
    // Keep the jitter budget this path taught us. A clean utterance relaxes
    // it slightly so a single bad network moment does not add latency
    // forever; an utterance that underran keeps the wider value it earned.
    // Converges on the smallest buffer that actually plays cleanly here.
    if (playbackUnderruns === 0) {
        playbackLeadSec = Math.max(playbackLeadSec * 0.9, PLAYBACK_LEAD_SEC);
    }
    try { localStorage.setItem(PLAYBACK_LEAD_KEY, playbackLeadSec.toFixed(3)); } catch { /* private mode */ }
    playbackUnderruns = 0;
}

// ─── Audio Capture Cleanup ───────────────────────────────────────────────────

function cleanupAudio() {
    if (workletNode) {
        workletNode.disconnect();
        workletNode = null;
    }
    if (sourceNode) {
        sourceNode.disconnect();
        sourceNode = null;
    }
    if (audioContext) {
        audioContext.close().catch(() => {});
        audioContext = null;
    }
    if (mediaStream) {
        mediaStream.getTracks().forEach(t => t.stop());
        mediaStream = null;
    }
}

// ─── UI Management ───────────────────────────────────────────────────────────

function showVoiceUI() {
    const voiceMode = document.getElementById('voice-mode');
    const inputRow = document.getElementById('input-row');
    if (voiceMode) voiceMode.classList.remove('hidden');
    if (inputRow) inputRow.classList.add('hidden');

    // Reset mute state
    isMuted = false;
    updateMuteButton();

    // Wire up buttons. The end button does different things depending
    // on mode — for ingest-playback we tear down only the TTS, not a
    // (nonexistent) WebSocket / mic stack.
    const endBtn = document.getElementById('voice-end-btn');
    if (endBtn) {
        endBtn.onclick = ingestPlaybackActive ? stopIngestPlayback : stopVoiceMode;
    }

    const muteBtn = document.getElementById('voice-mute-btn');
    if (muteBtn) {
        muteBtn.onclick = ingestPlaybackActive ? toggleIngestPlaybackMute : toggleMute;
    }

    // Narrator mode UI adjustments (paused narrator = normal voice mode)
    const isNarrator = state.narratorMode && !state.narratorPaused;
    const isVerbatim = isNarrator && state.agentSettings?.narrator_verbatim;
    const narratorCtrl = document.getElementById('voice-narrator-controls');
    const prevBtn = document.getElementById('voice-narrator-prev');
    const nextBtn = document.getElementById('voice-narrator-next');
    const slider = document.getElementById('voice-narrator-slider');

    if (ingestPlaybackActive) {
        // Single-doc TTS playback: pause + mute + close. No mic, no
        // chunk cycle — hide prev/next/slider but keep the pause button
        // visible via voice-narrator-controls.
        if (muteBtn) muteBtn.style.display = '';
        if (narratorCtrl) narratorCtrl.classList.remove('hidden');
        if (prevBtn) prevBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = 'none';
        if (slider) slider.style.display = 'none';
        // Reflect current ingest mute state on the mic icons (we reuse
        // them to indicate audio-out muted vs unmuted).
        isMuted = ingestPlaybackMuted;
        updateMuteButton();
        updatePauseButton('idle');
    } else if (dictationActive) {
        // Dictation mode: keep mute button (user may want to pause without closing),
        // hide narrator controls
        if (muteBtn) muteBtn.style.display = '';
        if (narratorCtrl) narratorCtrl.classList.add('hidden');
    } else if (isVerbatim) {
        // No mic in verbatim — hide mute button, show narrator controls
        if (muteBtn) muteBtn.style.display = 'none';
        if (narratorCtrl) narratorCtrl.classList.remove('hidden');
        if (prevBtn) prevBtn.style.display = '';
        if (nextBtn) nextBtn.style.display = '';
        if (slider) slider.style.display = '';
        renderNarratorSlider(true);
        updatePauseButton('idle');
    } else if (isNarrator) {
        // Non-verbatim: keep mute, add narrator controls
        if (muteBtn) muteBtn.style.display = '';
        if (narratorCtrl) narratorCtrl.classList.remove('hidden');
        if (prevBtn) prevBtn.style.display = '';
        if (nextBtn) nextBtn.style.display = '';
        if (slider) slider.style.display = '';
        renderNarratorSlider(false);
        updatePauseButton('idle');
    } else {
        // Normal voice mode
        if (muteBtn) muteBtn.style.display = '';
        if (narratorCtrl) narratorCtrl.classList.add('hidden');
    }

    // Wire narrator nav buttons
    const pauseBtn = document.getElementById('voice-narrator-pause');
    if (prevBtn) prevBtn.onclick = onNarratorPrev;
    if (nextBtn) nextBtn.onclick = onNarratorNext;
    if (pauseBtn) pauseBtn.onclick = toggleNarratorPause;

    // Scroll to last narrator response when entering narrator voice mode
    if (isNarrator) {
        scrollToLastNarratorMessage();
        updateNextButtonLabel();
    }
}

/**
 * Mute toggle for ingest read-aloud mode. There's no mic to mute here;
 * this flips the master gain on the narrator playback context so the
 * audio output goes silent (or comes back). Visual state piggybacks on
 * the mic-on/mic-off icons.
 */
function toggleIngestPlaybackMute() {
    ingestPlaybackMuted = !ingestPlaybackMuted;
    if (narratorAudio) {
        narratorAudio.muted = ingestPlaybackMuted;
    }
    isMuted = ingestPlaybackMuted;
    updateMuteButton();
}

/**
 * Show the voice UI in a TTS-only playback configuration for ingest
 * read-aloud. The pause/mute/close buttons are visible; the prev/next
 * chunk-nav and slider are hidden. No WebSocket, no mic.
 */
export function showIngestPlaybackUI() {
    ingestPlaybackActive = true;
    ingestPlaybackMuted = false;
    showVoiceUI();
}

/**
 * Tear down ingest read-aloud playback: stop the TTS stream and hide
 * the voice UI. Wired to the End (X) button when ingestPlaybackActive.
 */
export function stopIngestPlayback() {
    voiceGeneration++;
    stopNarratorTts();
    ingestPlaybackActive = false;
    ingestPlaybackMuted = false;
    hideVoiceUI();
    setState(VOICE_STATES.IDLE);
}

function toggleMute() {
    isMuted = !isMuted;
    updateMuteButton();

    // Mute is instant in EVERY phase — the guard on the audio sender reads
    // isMuted directly — so the UI has to say so in every phase too.
    // Previously only LISTENING and USER_SPEAKING updated the label, so
    // muting while "Transcribing…" looked like the tap had been ignored
    // (and muting the moment transcription starts is exactly when you want
    // it, to keep the room out of the next utterance).
    const statusText = document.querySelector('.voice-status-text');
    const ring = document.querySelector('.voice-ring');
    if (ring) ring.classList.toggle('muted', isMuted);
    if (statusText) {
        // Same rule as updateVoiceVisuals: mute confirms itself in words
        // EXCEPT while the machine is thinking, buffering or speaking,
        // where the phase is the thing worth watching. The button icon and
        // the ring still flip immediately, so the tap is acknowledged
        // either way. Unmuting restores the phase's own label WITHOUT
        // re-entering setState(), which would restart that phase's safety
        // timers.
        const phaseLabel = PHASE_LABELS[voiceState] || 'Listening...';
        statusText.textContent = (isMuted && !MUTE_YIELDS_TO.has(voiceState))
            ? 'Muted'
            : phaseLabel;
    }
}

// The label each phase shows when the mic is live. Kept beside toggleMute
// so unmuting can restore the right words without a state transition.
// Phases whose own label outranks "Muted": the machine is working and that
// progress is what the user is waiting on. Everything else — listening,
// hearing you, transcribing — is about the microphone, which is precisely
// what mute has something to say about.
const MUTE_YIELDS_TO = new Set([
    VOICE_STATES.PROCESSING,
    VOICE_STATES.BUFFERING,
    VOICE_STATES.ASSISTANT_SPEAKING,
]);

const PHASE_LABELS = {
    [VOICE_STATES.LISTENING]: 'Listening...',
    [VOICE_STATES.USER_SPEAKING]: 'Hearing you...',
    [VOICE_STATES.TRANSCRIBING]: 'Transcribing...',
    [VOICE_STATES.PROCESSING]: 'Thinking...',
    [VOICE_STATES.BUFFERING]: 'Buffering...',
    [VOICE_STATES.ASSISTANT_SPEAKING]: 'Speaking...',
    [VOICE_STATES.PAUSED]: 'Paused',
    [VOICE_STATES.CONNECTING]: 'Connecting...',
};

function updateMuteButton() {
    const muteBtn = document.getElementById('voice-mute-btn');
    if (!muteBtn) return;

    const micOn = muteBtn.querySelector('.mic-on-icon');
    const micOff = muteBtn.querySelector('.mic-off-icon');
    if (micOn) micOn.style.display = isMuted ? 'none' : '';
    if (micOff) micOff.style.display = isMuted ? '' : 'none';

    muteBtn.classList.toggle('muted', isMuted);
    muteBtn.title = isMuted ? 'Unmute microphone' : 'Mute microphone';
}

function hideVoiceUI() {
    const voiceMode = document.getElementById('voice-mode');
    const inputRow = document.getElementById('input-row');
    if (voiceMode) voiceMode.classList.add('hidden');
    if (inputRow) inputRow.classList.remove('hidden');

    // Reset narrator controls — including the prev/next/slider visibility
    // we may have toggled off for ingest-playback mode, so the next
    // narrator session starts with everything visible again.
    const narratorCtrl = document.getElementById('voice-narrator-controls');
    if (narratorCtrl) narratorCtrl.classList.add('hidden');
    const prevBtn = document.getElementById('voice-narrator-prev');
    const nextBtn = document.getElementById('voice-narrator-next');
    const slider = document.getElementById('voice-narrator-slider');
    if (prevBtn) prevBtn.style.display = '';
    if (nextBtn) nextBtn.style.display = '';
    if (slider) slider.style.display = '';
    const muteBtn = document.getElementById('voice-mute-btn');
    if (muteBtn) muteBtn.style.display = '';

    // Reset ingest-playback flag so any stale state doesn't bleed into
    // a subsequent narrator session.
    ingestPlaybackActive = false;
}

function setState(newState) {
    voiceState = newState;

    // Marks "a voice session is open" for the rest of the UI. Hands-free
    // changes what good behaviour looks like — new messages glide into
    // view instead of snapping, because nobody is holding the scrollbar.
    document.body.classList.toggle('voice-active', newState !== VOICE_STATES.IDLE);

    // Clear any existing stuck timer
    if (stuckTimer) {
        clearTimeout(stuckTimer);
        stuckTimer = null;
    }

    // Safety: if stuck in USER_SPEAKING for >90s without a transcript,
    // force back to LISTENING (server max utterance is 60s, so this is a
    // generous fallback for when the server-side safety doesn't fire)
    if (newState === VOICE_STATES.USER_SPEAKING) {
        stuckTimer = setTimeout(() => {
            if (voiceState === VOICE_STATES.USER_SPEAKING) {
                setState(VOICE_STATES.LISTENING);
            }
        }, 90000);
    }

    updateVoiceVisuals();
}

function updateVoiceVisuals() {
    const ring = document.querySelector('.voice-ring');
    const statusText = document.querySelector('.voice-status-text');
    const voiceBtn = document.getElementById('voice-btn');
    if (!ring) return;

    // Remove all state classes
    ring.className = 'voice-ring';

    switch (voiceState) {
        case VOICE_STATES.CONNECTING:
            ring.classList.add('connecting');
            if (statusText) statusText.textContent = 'Connecting...';
            break;
        case VOICE_STATES.LISTENING:
            if (dictationActive) {
                ring.classList.add('listening');
                if (statusText) statusText.textContent = 'Listening...';
            } else if (narratorVerbatimMode) {
                ring.classList.add('listening');
                if (statusText) statusText.textContent = 'Ready';
            } else if (isMuted) {
                ring.classList.add('muted');
                if (statusText) statusText.textContent = 'Muted';
            } else {
                ring.classList.add('listening');
                if (statusText) statusText.textContent = 'Listening...';
            }
            break;
        case VOICE_STATES.USER_SPEAKING:
            ring.classList.add('user-speaking');
            if (statusText) statusText.textContent = 'Hearing you...';
            break;
        case VOICE_STATES.TRANSCRIBING:
            ring.classList.add('processing');
            if (statusText) statusText.textContent = 'Transcribing...';
            break;
        case VOICE_STATES.PROCESSING:
            ring.classList.add('processing');
            if (statusText) statusText.textContent = 'Thinking...';
            break;
        case VOICE_STATES.BUFFERING:
            ring.classList.add('processing');
            if (statusText) statusText.textContent = 'Buffering...';
            break;
        case VOICE_STATES.RECONNECTING:
            ring.classList.add('reconnecting');
            if (statusText) {
                const attempt = Math.max(narratorReconnectAttempt, wsReconnectAttempt);
                statusText.textContent = attempt > 1
                    ? `Reconnecting… (${attempt}/${MAX_RECONNECT_ATTEMPTS})`
                    : 'Reconnecting…';
            }
            break;
        case VOICE_STATES.PAUSED:
            ring.classList.add('listening');  // Calm ring, not pulsing
            if (statusText) statusText.textContent = 'Paused';
            break;
        case VOICE_STATES.ASSISTANT_SPEAKING:
            ring.classList.add('assistant-speaking');
            if (statusText) statusText.textContent = 'Speaking...';
            break;
        case VOICE_STATES.IDLE:
            if (statusText) statusText.textContent = '';
            break;
    }

    // Mute survives every phase change. The switch above resets
    // ring.className wholesale, which would silently un-say a mute the
    // user had chosen, so the ring is re-marked last regardless of phase.
    //
    // The LABEL is different. Muting is what you do the moment
    // transcription starts, to keep the room out of the next utterance —
    // so the mic being off is the standing background fact for the rest of
    // that turn, while "Thinking", "Buffering" and "Speaking" are the
    // things actually changing. Letting "Muted" overwrite them left the
    // one word on screen through the entire reply, hiding the only
    // progress the user had. Those three phases keep their own words; when
    // they end, the label falls back to "Muted" on its own, because this
    // function runs on every phase change and isMuted has not moved.
    if (isMuted && voiceState !== VOICE_STATES.IDLE) {
        ring.classList.add('muted');
        if (statusText && !MUTE_YIELDS_TO.has(voiceState)) statusText.textContent = 'Muted';
    }

    if (voiceBtn) {
        voiceBtn.classList.toggle('active', voiceState !== VOICE_STATES.IDLE);
    }
}

// ─── Barge-In Helpers ────────────────────────────────────────────────────────

/**
 * Mark the last user message as cancelled — CSS hides it and all siblings after it
 * (including the assistant response that may not exist yet).
 */
function _markCancelledExchange() {
    const container = elements.chatContainer;
    if (!container) return;
    // Find the last user message and mark it
    const messages = container.querySelectorAll('.message.user');
    const lastUser = messages[messages.length - 1];
    if (lastUser) lastUser.classList.add('voice-cancelled');
}

/**
 * Undo a false barge-in: just remove the CSS class so the exchange is visible again.
 * Used when noise triggered speech_start but no real transcript followed.
 */
function _removeCancelledClass() {
    const container = elements.chatContainer;
    if (!container) return;
    container.querySelectorAll('.voice-cancelled').forEach(el => {
        el.classList.remove('voice-cancelled');
    });
}

/**
 * Remove all elements marked as voice-cancelled (and everything after them).
 */
function _removeCancelledExchange() {
    const container = elements.chatContainer;
    if (!container) return;
    const cancelled = container.querySelectorAll('.voice-cancelled');
    cancelled.forEach(el => {
        // Remove all siblings after this element
        while (el.nextElementSibling) {
            el.nextElementSibling.remove();
        }
        el.remove();
    });
}

// ─── Narrator Navigation & TTS ──────────────────────────────────────────────

const BATCH_STOPS = [1, 2, 3, 4, 5, 10, 15, 20, 30, 40, 50];
const LENGTH_LABELS = ['Short', 'Medium', 'Long', 'Very Long'];
const LENGTH_CHUNKS = [1, 3, 5, 10];

/**
 * Render the appropriate slider inside the voice narrator controls.
 * @param {boolean} isVerbatim - true for chunk batch slider, false for narration length slider
 */
function renderNarratorSlider(isVerbatim) {
    const container = document.getElementById('voice-narrator-slider');
    if (!container) return;

    if (isVerbatim) {
        const currentIdx = BATCH_STOPS.indexOf(state.narratorState.chunk_batch || 1);
        const idx = currentIdx >= 0 ? currentIdx : 0;
        container.innerHTML = `
            <label>Chunks: <strong>${BATCH_STOPS[idx]}</strong> <span class="info-tooltip" data-tooltip="Number of knowledge base chunks to combine per response. Higher values show more content at once.">?</span></label>
            <input type="range" min="0" max="${BATCH_STOPS.length - 1}" value="${idx}" step="1">
        `;
        const slider = container.querySelector('input');
        const label = container.querySelector('label');
        slider.addEventListener('input', () => {
            const val = BATCH_STOPS[parseInt(slider.value)];
            label.innerHTML = `Chunks: <strong>${val}</strong> <span class="info-tooltip" data-tooltip="Number of knowledge base chunks to combine per response. Higher values show more content at once.">?</span>`;
            state.narratorState.chunk_batch = val;
            saveNarratorProgress(state.sessionId, state.narratorState);
        });
    } else {
        const currentIdx = state.narratorState.narration_length ?? 1;
        container.innerHTML = `
            <label>Length: <strong>${LENGTH_LABELS[currentIdx]}</strong> <span class="info-tooltip" data-tooltip="Controls how much content the narrator covers per response. Higher values produce longer narrations.">?</span></label>
            <input type="range" min="0" max="${LENGTH_LABELS.length - 1}" value="${currentIdx}" step="1">
        `;
        const slider = container.querySelector('input');
        const label = container.querySelector('label');
        slider.addEventListener('input', () => {
            const idx = parseInt(slider.value);
            label.innerHTML = `Length: <strong>${LENGTH_LABELS[idx]}</strong> <span class="info-tooltip" data-tooltip="Controls how much content the narrator covers per response. Higher values produce longer narrations.">?</span>`;
            state.narratorState.narration_length = idx;
            saveNarratorProgress(state.sessionId, state.narratorState);
        });
    }
}

// Track which narrator message is currently "active" for prev/next navigation
let currentNarratorMessage = null;

/**
 * Stop all narrator speech — both WebSocket progressive TTS and HTTP narrator TTS.
 */
function stopAllNarratorSpeech() {
    // Stop HTTP narrator TTS (verbatim mode / re-reads)
    stopNarratorTts();
    // Stop WebSocket progressive TTS (non-verbatim mode)
    cleanupPlayback();
    // Cancel any in-flight progressive TTS — interrupt clears server-side audio queue
    if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'interrupt' }));
    }
    ttsSessionActive = false;
    ttsSentenceBuffer = '';
    ttsRawTail = '';
    ttsPendingChunk = '';
    ttsPendingChunk = '';
}

/**
 * Highlight the active narrator message and dim all others.
 */
export function highlightNarratorMessage(activeEl) {
    const all = getAllNarratorMessages();
    for (const el of all) {
        if (el === activeEl) {
            el.classList.add('narrator-active');
            el.classList.remove('narrator-dimmed');
        } else {
            el.classList.remove('narrator-active');
            el.classList.add('narrator-dimmed');
        }
    }
}

/**
 * Clear all narrator highlighting (e.g. when exiting voice mode).
 */
export function clearNarratorHighlight() {
    const all = document.querySelectorAll('.narrator-active, .narrator-dimmed');
    for (const el of all) {
        el.classList.remove('narrator-active', 'narrator-dimmed');
    }
}

/**
 * Get all narrator assistant messages in DOM order.
 */
function getAllNarratorMessages() {
    return [...document.querySelectorAll('.message.assistant')];
}

/**
 * Handle Next button — scroll to next existing message, or fetch new if at the end.
 */
async function onNarratorNext() {
    if (state.isStreaming) return;
    stopAllNarratorSpeech();

    const all = getAllNarratorMessages();

    if (all.length === 0) {
        // No messages yet — fetch first chunk
        await fetchNewNarratorChunk();
        return;
    }

    // Default to last message if not tracking one (e.g. after a fresh fetch)
    if (!currentNarratorMessage || !currentNarratorMessage.isConnected) {
        currentNarratorMessage = all[all.length - 1];
    }

    const idx = all.indexOf(currentNarratorMessage);

    // Find the next narrator chunk message (skip Q&A responses when advancing)
    let nextIdx = idx + 1;
    while (nextIdx < all.length && !all[nextIdx].dataset.narratorChunkId) {
        nextIdx++;
    }

    if (nextIdx < all.length) {
        // There's a next narrator message — scroll to it and re-read
        const next = all[nextIdx];
        currentNarratorMessage = next;
        next.scrollIntoView({ behavior: 'smooth', block: 'start' });
        highlightNarratorMessage(next);
        syncNarratorStateFromElement(next);
        updateNextButtonLabel();
        const text = next.querySelector('.message-content')?.textContent || '';
        await autoPlayNarratorTts(text);
    } else {
        // No more narrator messages — fetch new chunk from server
        currentNarratorMessage = all[all.length - 1];
        clearNarratorHighlight();
        await fetchNewNarratorChunk();
    }
}

/**
 * Handle Prev button — always scroll to the previous existing message (never fetch).
 */
async function onNarratorPrev() {
    if (state.isStreaming) return;
    stopAllNarratorSpeech();

    const all = getAllNarratorMessages();
    if (all.length === 0) return;

    // Default to last message if not tracking one
    if (!currentNarratorMessage || !currentNarratorMessage.isConnected) {
        currentNarratorMessage = all[all.length - 1];
    }

    const idx = all.indexOf(currentNarratorMessage);

    // Find previous narrator chunk message (skip Q&A responses)
    let prevIdx = idx - 1;
    while (prevIdx >= 0 && !all[prevIdx].dataset.narratorChunkId) {
        prevIdx--;
    }
    if (prevIdx < 0) return;  // No previous narrator message

    const prev = all[prevIdx];
    currentNarratorMessage = prev;
    prev.scrollIntoView({ behavior: 'smooth', block: 'start' });
    highlightNarratorMessage(prev);
    syncNarratorStateFromElement(prev);
    updateNextButtonLabel();
    const text = prev.querySelector('.message-content')?.textContent || '';
    await autoPlayNarratorTts(text);
}

/**
 * Fetch a new narrator chunk from the server.
 */
async function fetchNewNarratorChunk() {
    setState(VOICE_STATES.BUFFERING);
    elements.messageInput.value = '';
    await sendMessage();
    // Set currentNarratorMessage to the newly created message
    const all = getAllNarratorMessages();
    if (all.length > 0) {
        currentNarratorMessage = all[all.length - 1];
    }
    updateNextButtonLabel();
}

/**
 * Jump to a specific narrator chunk by its actual DB chunk_id.
 * If the chunk is in the chat OR within a multi-chunk message, scroll/play it.
 * Otherwise, update the SERVER position and fetch from there.
 */
export async function jumpToNarratorChunk(chunkId) {
    if (!state.narratorMode) return;
    stopAllNarratorSpeech();

    // Open voice mode so narrator nav (prev/next/slider) is visible.
    // Skip if already active. In verbatim mode use the playback-only path
    // (no mic capture), matching the toggleVoice dispatch above.
    if (!isVoiceActive()) {
        const isVerbatim = state.narratorMode && state.agentSettings?.narrator_verbatim;
        try {
            if (isVerbatim) {
                startVerbatimNarratorMode();
            } else {
                await startVoiceMode();
            }
        } catch (_) {}
    }

    const all = getAllNarratorMessages();

    // Find a message that covers this chunk (message may span multiple chunks)
    const covering = all.find(m => {
        const start = parseInt(m.dataset.narratorChunkId);
        const count = parseInt(m.dataset.narratorChunkCount) || 1;
        return !isNaN(start) && chunkId >= start && chunkId < start + count;
    });

    if (covering) {
        currentNarratorMessage = covering;
        covering.scrollIntoView({ behavior: 'smooth', block: 'start' });
        highlightNarratorMessage(covering);
        syncNarratorStateFromElement(covering);
        updateNextButtonLabel();
        const text = covering.querySelector('.message-content')?.textContent || '';
        await autoPlayNarratorTts(text);
    } else {
        // Not in chat — update server-side position FIRST, then fetch
        const { setNarratorPosition } = await import('./api.js');
        if (state.loadedAgent && state.narratorState.book && state.narratorState.kb_path) {
            try {
                await setNarratorPosition(
                    state.loadedAgent,
                    state.narratorState.book,
                    state.narratorState.kb_path,
                    chunkId
                );
            } catch (e) {
                console.error('[Narrator Jump] Failed to set position:', e);
            }
        }
        state.narratorState.chunk_id = chunkId;
        updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, chunkId);
        await fetchNewNarratorChunk();
        // After fetch, scroll to the new message
        const updated = getAllNarratorMessages();
        if (updated.length > 0) {
            const last = updated[updated.length - 1];
            last.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
}

/**
 * Sync narrator state from a message element (for header badge + position tracking).
 */
function syncNarratorStateFromElement(el) {
    const chunkId = parseInt(el.dataset.narratorChunkId) || 1;
    const chunkCount = parseInt(el.dataset.narratorChunkCount) || 1;
    state.narratorState.chunk_id = chunkId + chunkCount;
    if (el.dataset.narratorBook) state.narratorState.book = el.dataset.narratorBook;
    if (el.dataset.narratorKbPath) state.narratorState.kb_path = el.dataset.narratorKbPath;
    saveNarratorProgress(state.sessionId, state.narratorState);
    updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);
}

/**
 * Auto-play TTS for narrator text via HTTP streaming (same pattern as tts-player.js).
 * Used for both verbatim and re-reading existing messages.
 */
/**
 * Start a silent audio keepalive + Media Session to prevent mobile browsers
 * from suspending the page when the screen turns off during narrator playback.
 */
function startMediaKeepAlive(title) {
    // Silent <audio> loop — browsers treat <audio> element playback as "real media"
    // and won't suspend the page while it's playing
    // Phones only: desktops do not suspend the page, and the old zero-length
    // looping clip froze the browser's UI once the narration was paused
    // (see silent-audio.js).
    if (isMobileBrowser()) {
        if (!mediaKeepAliveAudio) {
            mediaKeepAliveAudio = new Audio(silentWavUrl());
            mediaKeepAliveAudio.loop = true;
            mediaKeepAliveAudio.volume = 0;
        }
        mediaKeepAliveAudio.play().catch(() => {});
    }

    // Media Session API — shows lock screen controls and signals active media
    if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
            title: title || 'Narration',
            artist: 'Ragsmith',
        });
        navigator.mediaSession.playbackState = 'playing';
        // Lock-screen / OS controls pause & resume the narration (resumable),
        // rather than tearing it down. The OS sends 'pause' while playing and
        // 'play' while paused.
        navigator.mediaSession.setActionHandler('pause', () => {
            if (narratorAudio && !narratorAudio.paused) toggleNarratorPause();
        });
        navigator.mediaSession.setActionHandler('play', () => {
            if (narratorAudio && narratorAudio.paused) toggleNarratorPause();
        });
        navigator.mediaSession.setActionHandler('nexttrack', () => onNarratorNext());
        navigator.mediaSession.setActionHandler('previoustrack', () => onNarratorPrev());
    }
}

// ─── Crash Recovery ──────────────────────────────────────────────────────────

/**
 * Schedule a reconnect attempt for the narrator HTTP TTS stream.
 * Uses exponential backoff: 1s, 2s, 4s, 8s, 16s — capped at MAX_RECONNECT_ATTEMPTS.
 * After max attempts, surfaces a manual Resume button instead of looping.
 */
function scheduleNarratorReconnect() {
    cancelPendingNarratorReconnect();
    if (intentionalStop || !lastNarratorText) return;

    narratorReconnectAttempt++;
    if (narratorReconnectAttempt > MAX_RECONNECT_ATTEMPTS) {
        showNarratorResumeButton();
        return;
    }

    setState(VOICE_STATES.RECONNECTING);
    const delay = Math.min(16000, 1000 * Math.pow(2, narratorReconnectAttempt - 1));
    console.log(`[Voice] Narrator reconnect attempt ${narratorReconnectAttempt}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);

    narratorReconnectTimer = setTimeout(() => {
        narratorReconnectTimer = null;
        if (intentionalStop || !lastNarratorText) return;
        // Re-attempt the SAME chunk. Whisper-equivalent: server re-synthesizes
        // from the start; user hears the chunk again, which is fine for narrator.
        autoPlayNarratorTts(lastNarratorText).catch(err => {
            console.error('[Voice] Narrator reconnect attempt failed:', err);
        });
    }, delay);
}

function cancelPendingNarratorReconnect() {
    if (narratorReconnectTimer) {
        clearTimeout(narratorReconnectTimer);
        narratorReconnectTimer = null;
    }
}

/**
 * After the auto-reconnect retries fail, show a one-click Resume button
 * inside the voice UI. Clicking it resets the counter and tries again.
 */
function showNarratorResumeButton() {
    setState(VOICE_STATES.IDLE);
    const statusText = document.querySelector('.voice-status-text');
    if (statusText) {
        statusText.textContent = '';
        const btn = document.createElement('button');
        btn.textContent = 'Connection lost — Resume';
        btn.className = 'voice-resume-btn';
        btn.onclick = () => {
            narratorReconnectAttempt = 0;
            statusText.textContent = '';
            if (lastNarratorText) {
                autoPlayNarratorTts(lastNarratorText).catch(err => {
                    console.error('[Voice] Manual resume failed:', err);
                });
            }
        };
        statusText.appendChild(btn);
    }
}

/**
 * Schedule a reconnect attempt for the listening WebSocket. Same backoff
 * curve as the narrator reconnect. On success the mic + worklet are
 * reattached without disturbing the playback context.
 */
function scheduleWsReconnect() {
    if (wsReconnectTimer) clearTimeout(wsReconnectTimer);
    if (intentionalStop) return;

    wsReconnectAttempt++;
    if (wsReconnectAttempt > MAX_RECONNECT_ATTEMPTS) {
        addMessage('system', 'Voice connection lost. Tap the mic to reconnect.');
        setState(VOICE_STATES.IDLE);
        return;
    }

    setState(VOICE_STATES.RECONNECTING);
    const delay = Math.min(16000, 1000 * Math.pow(2, wsReconnectAttempt - 1));
    console.log(`[Voice] WS reconnect attempt ${wsReconnectAttempt}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);

    wsReconnectTimer = setTimeout(async () => {
        wsReconnectTimer = null;
        if (intentionalStop) return;
        try {
            await reopenVoiceWebSocket();
            wsReconnectAttempt = 0;  // success
            setState(VOICE_STATES.LISTENING);
        } catch (e) {
            console.error('[Voice] WS reconnect failed:', e);
            scheduleWsReconnect();  // try again with the next backoff slot
        }
    }, delay);
}

async function reopenVoiceWebSocket() {
    return new Promise((resolve, reject) => {
        try {
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsUrl = `${wsProtocol}//${window.location.host}/voice/stream${voiceSocketAuth('?')}`;
            const newWs = new WebSocket(wsUrl);
            newWs.binaryType = 'arraybuffer';
            const openTimeout = setTimeout(() => {
                try { newWs.close(); } catch (_) {}
                reject(new Error('WebSocket open timeout'));
            }, 8000);
            newWs.onopen = () => {
                clearTimeout(openTimeout);
                ws = newWs;
                ws.send(JSON.stringify({ type: 'start' }));
                ws.onmessage = (e) => {
                    if (e.data instanceof ArrayBuffer) {
                        handleBinaryAudio(e.data);
                    } else {
                        handleServerMessage(JSON.parse(e.data));
                    }
                };
                ws.onclose = () => {
                    if (!intentionalStop && voiceState !== VOICE_STATES.IDLE) {
                        scheduleWsReconnect();
                    }
                };
                ws.onerror = (err) => {
                    console.error('[Voice] WebSocket error:', err);
                };
                resolve();
            };
            newWs.onerror = (err) => {
                clearTimeout(openTimeout);
                reject(err);
            };
        } catch (e) {
            reject(e);
        }
    });
}

/**
 * Stop the media keepalive and clear Media Session.
 */
function stopMediaKeepAlive() {
    if (mediaKeepAliveAudio) {
        mediaKeepAliveAudio.pause();
    }
    if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none';
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.setActionHandler('previoustrack', null);
    }
}

export async function autoPlayNarratorTts(text) {
    stopNarratorTts();
    clearInterChunkGap();  // New TTS arriving — kill the inter-chunk buffering timer
    cancelPendingNarratorReconnect();  // Fresh play: drop any queued reconnect attempt
    voiceGeneration++;
    const gen = voiceGeneration;
    const cleanText = cleanTextForTts(text);
    if (!cleanText) {
        if (narratorVerbatimMode) setState(VOICE_STATES.LISTENING);
        return;
    }

    // Remember the text for resume-on-crash — restored on a successful reconnect.
    lastNarratorText = text;
    narratorStallDetected = false;

    setState(VOICE_STATES.BUFFERING);
    startMediaKeepAlive(text.substring(0, 60));

    try {
        // Prepare the render, then play it through an <audio> MediaElement.
        // The browser's media pipeline (not the JS event loop) drives the
        // fetch / buffer / decode, so playback survives background tabs, app
        // switches and the display turning off — the way YouTube does it. The
        // <audio> also handles its own stall/buffering, so we just map its
        // native events onto the existing UI/state machine.
        const voice = getSelectedVoice();
        narratorAbortController = new AbortController();
        const prep = await fetch(`${API_BASE}/voice/tts/prepare`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ text: cleanText, voice, ...getTTSOptions(voice) }),
            signal: narratorAbortController.signal,
        });
        if (!prep.ok) throw new Error(`TTS prepare failed: ${prep.status}`);
        const { id } = await prep.json();
        if (voiceGeneration !== gen) return;  // superseded while preparing

        const audio = getNarratorAudio();
        audio.muted = ingestPlaybackMuted;

        audio.onplaying = () => {
            if (voiceGeneration !== gen) return;
            narratorReconnectAttempt = 0;  // healthy playback
            setState(VOICE_STATES.ASSISTANT_SPEAKING);
            updatePauseButton('playing');
            if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
        };
        audio.onwaiting = () => {
            if (voiceGeneration !== gen) return;
            if (voiceState === VOICE_STATES.ASSISTANT_SPEAKING) setState(VOICE_STATES.BUFFERING);
        };
        audio.onended = () => {
            if (voiceGeneration !== gen) return;
            updatePauseButton('idle');
            if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'none';
            // Next chunk usually follows — keep the media session warm and show
            // "Buffering…" until the next TTS arrives or the gap timer expires.
            enterInterChunkBuffering();
        };
        audio.onerror = () => {
            if (voiceGeneration !== gen || intentionalStop) return;
            console.error('[Voice] Narrator audio error', audio.error);
            updatePauseButton('idle');
            if (lastNarratorText) scheduleNarratorReconnect();
            else if (voiceState !== VOICE_STATES.IDLE) setState(VOICE_STATES.LISTENING);
        };

        audio.src = `${API_BASE}/voice/tts/stream-mp3/${id}${narratorAuthParam()}`;
        try {
            await audio.play();
        } catch (playErr) {
            // play() rejects when interrupted by a newer src/stop — only surface
            // it if this is still the current play and not an intentional stop.
            if (voiceGeneration === gen && !intentionalStop && playErr.name !== 'AbortError') {
                throw playErr;
            }
        }
    } catch (e) {
        clearInterChunkGap();
        if (e.name === 'AbortError') return;  // intentional stop / superseded
        console.error('[Voice] Narrator TTS error:', e);
        updatePauseButton('idle');
        // Prepare/play failed → attempt resume for the same chunk.
        if (voiceGeneration === gen && !intentionalStop && lastNarratorText) {
            scheduleNarratorReconnect();
            return;
        }
        if (voiceGeneration === gen && voiceState !== VOICE_STATES.IDLE) {
            setState(VOICE_STATES.LISTENING);
        }
    }
}

/**
 * After a narrator chunk finishes playing, show "Buffering…" while we wait
 * for the next chunk to start. If nothing arrives within INTER_CHUNK_GAP_MS,
 * fall back to LISTENING so the user isn't stuck looking at a buffering
 * spinner forever (e.g. they tapped pause or there's no next chunk).
 */
function enterInterChunkBuffering() {
    clearInterChunkGap();
    setState(VOICE_STATES.BUFFERING);
    interChunkGapTimer = setTimeout(() => {
        interChunkGapTimer = null;
        if (voiceState === VOICE_STATES.BUFFERING) {
            setState(VOICE_STATES.LISTENING);
        }
    }, INTER_CHUNK_GAP_MS);
}

function clearInterChunkGap() {
    if (interChunkGapTimer) {
        clearTimeout(interChunkGapTimer);
        interChunkGapTimer = null;
    }
}



/**
 * Update the pause/resume button state.
 * @param {'idle'|'playing'|'paused'|'resume'} mode
 */
function updatePauseButton(mode) {
    const btn = document.getElementById('voice-narrator-pause');
    const pauseIcon = document.getElementById('narrator-tts-pause-icon');
    const playIcon = document.getElementById('narrator-tts-play-icon');
    const label = document.getElementById('narrator-tts-pause-label');
    if (!btn) return;

    if (mode === 'idle') {
        btn.disabled = true;
        btn.style.opacity = '0.3';
        if (pauseIcon) pauseIcon.style.display = '';
        if (playIcon) playIcon.style.display = 'none';
        if (label) label.textContent = 'Pause';
    } else if (mode === 'playing') {
        btn.disabled = false;
        btn.style.opacity = '';
        if (pauseIcon) pauseIcon.style.display = '';
        if (playIcon) playIcon.style.display = 'none';
        if (label) label.textContent = 'Pause';
    } else if (mode === 'paused') {
        btn.disabled = false;
        btn.style.opacity = '';
        if (pauseIcon) pauseIcon.style.display = 'none';
        if (playIcon) playIcon.style.display = '';
        if (label) label.textContent = 'Resume';
    } else if (mode === 'resume') {
        // Show resume state (for reload recovery)
        btn.disabled = false;
        btn.style.opacity = '';
        if (pauseIcon) pauseIcon.style.display = 'none';
        if (playIcon) playIcon.style.display = '';
        if (label) label.textContent = 'Resume';
    }
}

/**
 * Lazily create the persistent <audio> element used for narrator playback.
 * One element is reused for every chunk so the browser keeps a stable media
 * session (and so background/lock-screen playback is uninterrupted).
 */
function getNarratorAudio() {
    if (!narratorAudio) {
        narratorAudio = new Audio();
        narratorAudio.preload = 'auto';
    }
    return narratorAudio;
}

/**
 * Auth query param for the <audio src>. An <audio> element can't set custom
 * headers, so the auth middleware accepts the token via query string:
 * ?token= (session), ?u= (container), or ?c= (capsule). Empty on localhost.
 */
/**
 * The same credential for the voice socket. A WebSocket cannot set headers
 * either, and the socket used to accept anyone who knew its address.
 * `lead` is '?' when this starts the query string and '&' when it joins one.
 */
function voiceSocketAuth(lead) {
    const param = narratorAuthParam();
    return param ? lead + param.slice(1) : '';
}

function narratorAuthParam() {
    const t = getAuthToken();
    if (t) return `?token=${encodeURIComponent(t)}`;
    if (state.containerMode && state.containerToken) return `?u=${encodeURIComponent(state.containerToken)}`;
    if (state.capsuleMode && state.capsuleToken) return `?c=${encodeURIComponent(state.capsuleToken)}`;
    return '';
}

/**
 * Toggle pause/resume of narrator TTS playback (the <audio> element).
 */
function toggleNarratorPause() {
    if (!narratorAudio || !narratorAudio.src) return;

    if (!narratorAudio.paused) {
        narratorAudio.pause();
        updatePauseButton('paused');
        setState(VOICE_STATES.PAUSED);
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
    } else {
        narratorAudio.play().catch(() => {});
        updatePauseButton('playing');
        setState(VOICE_STATES.ASSISTANT_SPEAKING);
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
    }
}

/**
 * Stop narrator TTS playback and clean up.
 */
function stopNarratorTts() {
    stopMediaKeepAlive();
    updatePauseButton('idle');
    if (narratorAbortController) {
        narratorAbortController.abort();
        narratorAbortController = null;
    }
    if (narratorAudio) {
        // Drop handlers so the in-flight element doesn't drive state for a
        // superseded play, then pause and abort the streaming fetch.
        narratorAudio.onplaying = narratorAudio.onwaiting = narratorAudio.onended = narratorAudio.onerror = null;
        try { narratorAudio.pause(); } catch (_) {}
        narratorAudio.removeAttribute('src');
        try { narratorAudio.load(); } catch (_) {}  // cancels the underlying network request
    }
}

/**
 * Check if voice narrator mode is active (either verbatim or non-verbatim with narrator).
 */
export function isVoiceNarratorActive() {
    return voiceState !== VOICE_STATES.IDLE && state.narratorMode && !state.narratorPaused;
}

// ─── Utility ─────────────────────────────────────────────────────────────────

function int16ToBase64(int16Array) {
    const bytes = new Uint8Array(int16Array.buffer, int16Array.byteOffset, int16Array.byteLength);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

/**
 * Check if voice mode is currently active.
 */
export function isVoiceActive() {
    return voiceState !== VOICE_STATES.IDLE;
}

// ─── Sound Effects ──────────────────────────────────────────────────────────

/**
 * Preload sound effect WAV files into AudioBuffers for instant playback.
 * Called during init spinner ("Loading voice...") phase.
 */
export async function preloadSoundEffects() {
    try {
        sfxContext = new AudioContext();
        for (const name of ['bell', 'search']) {
            const res = await fetch(`/sounds/${name}.wav`);
            if (!res.ok) continue;
            const arrayBuf = await res.arrayBuffer();
            sfxBuffers[name] = await sfxContext.decodeAudioData(arrayBuf);
        }
    } catch {
    }
}

/**
 * Play a preloaded sound effect with optional pitch shift.
 * Awaits AudioContext resume to avoid dropped sounds on suspended contexts.
 */
async function playSfx(name, rate = 1.0) {
    if (!sfxContext || !sfxBuffers[name]) return;
    if (sfxContext.state === 'suspended') {
        await sfxContext.resume();
    }
    const source = sfxContext.createBufferSource();
    source.buffer = sfxBuffers[name];
    source.playbackRate.value = rate;
    const gain = sfxContext.createGain();
    gain.gain.value = 0.5;
    source.connect(gain).connect(sfxContext.destination);
    source.start();
}

/**
 * Called by chat.js when tool/search events fire.
 * Plays search whoosh or progressive-pitch bell for tool calls.
 * Bells are queued and staggered so parallel tool completions
 * produce a pleasant ascending sequence instead of a cacophony.
 */
let bellQueue = [];
let bellPlaying = false;
const BELL_SPACING_MS = 300;

function drainBellQueue() {
    if (bellPlaying || bellQueue.length === 0) return;
    bellPlaying = true;
    const rate = bellQueue.shift();
    playSfx('bell', rate);
    setTimeout(() => {
        bellPlaying = false;
        drainBellQueue();
    }, BELL_SPACING_MS);
}

export function onVoiceToolEvent(type) {
    if (type === 'search') {
        playSfx('search');
    } else if (type === 'tool') {
        toolCallCount++;
        const rate = 0.8 + (toolCallCount - 1) * 0.15;
        bellQueue.push(rate);
        drainBellQueue();
    }
}

/**
 * Reset the progressive tool call pitch counter (call at start of each new prompt).
 */
export function resetToolCallPitch() {
    toolCallCount = 0;
    bellQueue = [];
    bellPlaying = false;
}
